import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ABILITIES as ABILITIES_ALL } from './Character';
import { getSkinSrc } from './SeasonalSkins';
import {
  getUserBannerStyle,
  getUserNameCosmetics,
} from './GameNameStyles';
import '../index.css';

/** ===== Layout & Timing ===== */
const ADS_ENABLED = false;

const DISABLE_MATCHMAKING_SKIP = true;

const TURN_SPEED = 3.0;

const NOTICE_EXIT_MS = 350;
const MATCH_FOUND_PAUSE_MS = 2500; // faster intro

/** ===== Combat Tuning ===== */
const K_DEF_MIT = 14; // diminishing-returns constant for defense
const MAX_CHUNK_PCT = 0.16; // max % of target maxHP a single hit can deal
const BURN_TICK = 2;
const BLEED_TICK = 2;
const BURN_START = 2; // how many ticks to start with
const BLEED_START = 3;

const DAILY_WIN_AD_LIMIT = 1;
const DAILY_LOSS_AD_LIMIT = 1;

/** ===== Animation Durations (seconds) ===== */
const PROJ_TRAVEL_S = 0.36; // projectile
const IMPACT_LINGER_S = 0.42; // burst lifetime
const PRE_HIT_PAUSE_S = 0.12; // windup before shot

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const toId = (name) =>
  String(name || '')
    .trim()
    .toLowerCase();
const has = (unit, id) => unit?.abilitiesSet?.has(toId(id));

const CHESTS_META = {
  wood: { key: "wood", label: "WOOD", image: "chest1.png" },
  silver: { key: "silver", label: "SILVER", image: "chest2.png" },
  golden: { key: "golden", label: "GOLDEN", image: "chest3.png" },
  diamond: { key: "diamond", label: "DIAMOND", image: "chest4.png" },
};

function normalizeChestKey(k) {
  const s = String(k ?? "").trim().toLowerCase();
  return CHESTS_META[s] ? s : "wood";
}

const norm = (v) => String(v ?? "").trim().toLowerCase();

function getSecondsLeft(expiresAt) {
  if (!expiresAt) return 0;

  // Force UTC interpretation
  const iso = String(expiresAt).endsWith('Z')
    ? expiresAt
    : `${expiresAt}Z`;

  const endMs = Date.parse(iso);
  const nowMs = Date.now();

  if (!Number.isFinite(endMs)) return 0;

  return Math.max(0, Math.floor((endMs - nowMs) / 1000));
}

function formatCountdown(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function getTodayUTCKey() {
  const now = new Date();

  return [
    now.getUTCFullYear(),
    String(now.getUTCMonth() + 1).padStart(2, "0"),
    String(now.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function getBattleAdStorageKey(type) {
  return `battle_ad_${type}_${getTodayUTCKey()}`;
}

function getBattleAdUsage(type) {
  return Number(
    localStorage.getItem(
      getBattleAdStorageKey(type)
    ) || 0
  );
}

function incrementBattleAdUsage(type) {
  const key = getBattleAdStorageKey(type);

  const current = Number(
    localStorage.getItem(key) || 0
  );

  localStorage.setItem(
    key,
    String(current + 1)
  );
}

function getRewardType(reward) {
  // tenta vários formatos comuns
  const raw =
    reward?.type ??
    reward?.rewardType ??
    reward?.currency ??
    reward?.kind ??
    reward?.item?.type ??
    reward?.itemType ??
    null;

  const t = norm(raw);

  // também detecta pelo nome caso venha "3500 Sparks Prize"
  const name = norm(reward?.name ?? reward?.itemName ?? reward?.label ?? "");
  if (!t && name.includes("sparks")) return "sparks";
  if (!t && name.includes("runes")) return "runes";
  if (!t && name.includes("spins")) return "spins";
  if (!t && name.includes("booster")) return "booster";
  if (!t && name.includes("seasonal_accessory")) return "seasonal_accessory";
  if (!t && name.includes("ability")) return "ability";
  if (!t && name.includes("card")) return "card";
  if (!t && name.includes("evolve")) return "evolve";

  // normalizações
  if (t.includes("spark")) return "sparks";
  if (t.includes("rune")) return "runes";
  if (t.includes("spin")) return "spins";
  if (t.includes("booster")) return "booster";
  if (t.includes("seasonal_accessory")) return "seasonal_accessory";
  if (t.includes("ability")) return "ability";
  if (t.includes("card")) return "card";
  if (t.includes("evolve")) return "evolve";

  return t || "";
}

function getRewardIcon(reward) {
  const t = getRewardType(reward);

  if (t === "sparks") return "/sparks.png";
  if (t === "runes") return "/runes.png";
  if (t === "spins") return "/fortuneicon.png";
  if (t === "booster") return "/energy.png";
  if (t === "seasonal_accessory") return "/fragment.png";
  if (t === "ability") return "/fightlog.png";
  if (t === "card") return "/common.png";
  if (t === "evolve") return "/evolveicon.png";

  if (reward?.image) return `/${String(reward.image).replace(/^\//, "")}`;

  return "/pack.png";
}


function getChestMetaFromChest(chestLike) {
  // chestLike pode ser objeto (slot) com chestType, ou string "wood", etc
  const key = normalizeChestKey(chestLike?.chestType ?? chestLike?.type ?? chestLike);
  return CHESTS_META[key];
}

function IconPause({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

function IconPlay({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M8 5v14l11-7-11-7z" />
    </svg>
  );
}

function IconFastForward({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#fff"
      stroke="currentColor"
      strokeWidth="0"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M13 12 3 6v12l10-6z" />
      <path d="M21 12 11 6v12l10-6z" />
    </svg>
  );
}

function normalizeUnitFromApi(raw) {
  if (!raw || typeof raw !== 'object') return null;

  // hard requirements from backend
  if (
    !raw.id ||
    raw.attack === undefined ||
    raw.defense === undefined ||
    (raw.maxHp === undefined && raw.hp === undefined) ||
    !raw.side ||
    !raw.rarity ||
    !raw.name
  ) {
    return null;
  }

  const list = Array.isArray(raw.abilities)
    ? raw.abilities.map((a) => (typeof a === 'string' ? a : a?.name)).filter(Boolean)
    : [];

  const seasonalAccessory =
    raw.seasonal_accessory && typeof raw.seasonal_accessory === 'object'
      ? raw.seasonal_accessory
      : null;

  const seasonName =
    seasonalAccessory?.season_name ||
    raw.season_name ||
    null;

  const normalizedCharacter =
    raw.character && typeof raw.character === 'object'
      ? raw.character
      : null;

  const unitForLayers = {
    character: normalizedCharacter,
    seasonal_accessory: seasonalAccessory,
    season_name: seasonName,
  };

  const seasonalLayers = normalizedCharacter
    ? characterLayersFrom(unitForLayers)
    : [];

const source = String(raw.serverMeta?.source || raw.source || '').toLowerCase();
const isDefenseDeck = source === 'defense_deck';

const displayStats =
  isDefenseDeck
    ? null
    : raw.displayStats || raw.baseStats || null;

const visualAttack = Number(
  isDefenseDeck
    ? raw.attack
    : raw.visualAttack ?? displayStats?.attack ?? raw.attack
);

const visualDefense = Number(
  isDefenseDeck
    ? raw.defense
    : raw.visualDefense ?? displayStats?.defense ?? raw.defense
);

const visualHealth = Number(
  isDefenseDeck
    ? raw.maxHp ?? raw.hp
    : raw.visualHealth ?? displayStats?.health ?? raw.maxHp ?? raw.hp
);

return {
  id: String(raw.id),
  name: String(raw.name),
  rarity: String(raw.rarity),

  level:
  raw.level ??
  raw.cardLevel ??
  raw.character?.level ??
  raw.serverMeta?.level ??
  null,

  wins:
    raw.wins ??
    raw.battleWins ??
    raw.serverMeta?.wins ??
    raw.serverMeta?.battleWins ??
    null,

  attack: Number(raw.attack),
  defense: Number(raw.defense),
  maxHp: Number(raw.maxHp ?? raw.hp),
  hp: Number(raw.hp ?? raw.maxHp),

  visualAttack,
  visualDefense,
  visualHealth,

  displayStats: {
    attack: visualAttack,
    defense: visualDefense,
    health: visualHealth,
  },

  baseStats: {
    attack: visualAttack,
    defense: visualDefense,
    health: visualHealth,
  },

  abilities: list,
  abilitiesSet: new Set(list.map(toId)),
  side: String(raw.side),

  character: normalizedCharacter,

  seasonal_accessory: seasonalAccessory,
  season_name: seasonName,

  characterLayers:
    seasonalLayers.length > 0
      ? seasonalLayers
      : Array.isArray(raw.characterLayers)
        ? raw.characterLayers
        : [],

  art: typeof raw.art === 'string' ? raw.art : '',
  serverMeta: raw.serverMeta || null,
};
}

/* --------------------------------- Assets preload --------------------------------- */
function usePreloadImages(urls) {
  const cacheRef = useRef([]);
  useEffect(() => {
    cacheRef.current = urls.map((src) => {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = src;
      return img;
    });
  }, [urls]);
}

const OUT_OF_SPARKS_TEXT = 'No funds'; // ou 'Sem Sparks suficientes' / 'Not enough Sparks'

function SparkHint({ show, text = OUT_OF_SPARKS_TEXT }) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98, filter: 'blur(2px)' }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '100%',
            transform: 'translate(-50%, -8px)',
            padding: '6px 10px',
            borderRadius: 10,
            fontSize: 13,
            color: '#fff',
            background: 'rgba(0,0,0,0.75)',
            border: '1px solid rgba(255,255,255,0.25)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 20,
          }}
          aria-live="polite"
        >
          {text}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const SparkGuardButton = React.forwardRef(function SparkGuardButton(
  { user, onConfirm, children, hintText = OUT_OF_SPARKS_TEXT, style, ...props },
  ref,
) {
  const [showHint, setShowHint] = React.useState(false);
  const timerRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  const getUserSparks = (u) => Number(u?.sparks ?? u?.credits ?? u?.balance ?? 0);
  const hasSparks = Number.isFinite(getUserSparks(user)) ? getUserSparks(user) > 0 : true;

  const revealHint = (ms = 1400) => {
    setShowHint(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setShowHint(false), ms);
  };

  // expõe métodos via ref: focus() e showHint()
  React.useImperativeHandle(ref, () => ({
    focus: () => buttonRef.current?.focus(),
    showHint: (ms) => revealHint(ms),
  }));

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasSparks) {
      onConfirm?.();
      return;
    }
    revealHint();
  };

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  const isTrulyDisabled = !hasSparks || props.disabled;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <SparkHint show={showHint} text={hintText} />
      <button
        ref={buttonRef}
        onClick={handleClick}
        aria-disabled={isTrulyDisabled}
        disabled={isTrulyDisabled}
        style={{
          minWidth: 120,
          opacity: hasSparks ? 1 : 0.7,
          cursor: hasSparks ? 'pointer' : 'not-allowed',
          ...style,
        }}
        {...props}
      >
        {children}
      </button>
    </div>
  );
});

/* ------------------ Replace projectile render with this ------------------ */

function ProjectileSprite({ type = 'good', size = 22, direction = 'right' }) {
  const isLeft = direction === 'left';
  const flip = isLeft ? 'scaleX(-1)' : 'scaleX(1)';

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        transform: flip,
      }}
    >
      {type === 'bad' ? (
        /* --- icy shard --- */
        <>
          <div
            style={{
              width: size * 0.6,
              height: size * 0.6,
              borderRadius: 4,
              transform: 'rotate(22deg)',
              background: 'linear-gradient(180deg, rgba(200,240,255,1), rgba(150,200,255,0.95))',
              boxShadow: '0 0 10px rgba(150,210,255,0.9), 0 0 2px rgba(120,180,255,0.6)',
              marginLeft: size * 0.2,
              marginTop: size * 0.2,
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: -size * 1.6,
              top: '50%',
              width: size * 2.4,
              height: Math.max(8, size * 0.6),
              transform: 'translateY(-50%) rotate(-8deg)',
              borderRadius: 999,
              background:
                'linear-gradient(90deg, rgba(180,230,255,0), rgba(160,220,255,0.55), rgba(180,230,255,0))',
              filter: 'blur(6px)',
              opacity: 0.95,
            }}
          />
        </>
      ) : (
        /* --- warm fireball --- */
        <>
          <div
            style={{
              position: 'absolute',
              left: -size * 1.25,
              top: '50%',
              width: size * 2.4,
              height: Math.max(8, size * 0.6),
              transform: 'translateY(-50%) rotate(-6deg)',
              borderRadius: 999,
              background:
                'linear-gradient(90deg, rgba(255,120,0,0), rgba(255,200,0,0.75), rgba(255,120,0,0))',
              filter: 'blur(4px)',
              opacity: 0.95,
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: -size * 1.8,
              top: '50%',
              width: size * 3.4,
              height: Math.max(12, size * 0.9),
              transform: 'translateY(-50%) rotate(-8deg)',
              borderRadius: 999,
              background:
                'linear-gradient(90deg, rgba(255,90,0,0), rgba(255,140,0,0.35), rgba(255,90,0,0))',
              filter: 'blur(8px)',
              opacity: 0.95,
            }}
          />
          <div
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 30% 30%, #fff, #ffd76b 30%, #ff8b2b 70%)',
              boxShadow: '0 0 18px rgba(255,180,80,0.9), 0 0 6px rgba(255,220,140,0.6)',
            }}
          />
        </>
      )}
    </div>
  );
}

function ImpactBurst({ x, y, type = 'good', motionOn = true }) {
  const count = 12;
  const sparks = Array.from({ length: count }, (_, i) => i);
  const isBad = type === 'bad';

  const scaleDur = (s) => (motionOn ? s : 0); // <<— replaces A(...)

  const glow = isBad ? 'rgba(120,180,255,0.8)' : 'rgba(255,170,60,0.85)';
  const ringBorder = isBad ? 'rgba(180,230,255,0.9)' : 'rgba(255,210,120,0.9)';
  const coreBg = isBad
    ? 'radial-gradient(circle, rgba(233,246,255,1) 0%, rgba(159,210,255,0.2) 70%)'
    : 'radial-gradient(circle, rgba(255,244,204,1) 0%, rgba(255,181,107,0.2) 70%)';

  return (
    <motion.div
      initial={{ x, y, opacity: 0, scale: 0.9 }}
      animate={{ x, y, opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        pointerEvents: 'none',
        zIndex: 5,
        filter: `drop-shadow(0 0 10px ${glow})`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <motion.div
          initial={{ scale: 0.2, opacity: 0.9 }}
          animate={{ scale: 1.3, opacity: 0 }}
          transition={{
            duration: scaleDur(0.35 * TURN_SPEED),
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            left: -18,
            top: -18,
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: `2px solid ${ringBorder}`,
          }}
        />
        <motion.div
          initial={{ scale: 0.6, opacity: 1 }}
          animate={{ scale: 1.4, opacity: 0 }}
          transition={{
            duration: scaleDur(0.22 * TURN_SPEED),
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            left: -10,
            top: -10,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: coreBg,
          }}
        />
        {sparks.map((i) => {
          const angle = (i / count) * Math.PI * 2 + (Math.random() * 0.6 - 0.3);
          const dist = 32 + Math.random() * 12;
          const tx = Math.cos(angle) * dist;
          const ty = Math.sin(angle) * dist;
          const sz = 4 + Math.random() * 3;
          const dur = 0.35 + Math.random() * 0.18;
          return (
            <motion.span
              key={i}
              initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
              animate={{ x: tx, y: ty, opacity: 0, scale: 0.9 }}
              transition={{
                duration: scaleDur(dur * TURN_SPEED),
                ease: 'easeOut',
              }}
              style={{
                position: 'absolute',
                left: -sz / 2,
                top: -sz / 2,
                width: sz,
                height: sz,
                borderRadius: 2,
                background: isBad
                  ? 'linear-gradient(180deg, #e9f6ff, #9fd2ff)'
                  : 'linear-gradient(180deg, #fff4cc, #ffb56b)',
                boxShadow: `0 0 8px ${glow}`,
              }}
            />
          );
        })}
      </div>
    </motion.div>
  );
}

function makeZigZag(from, to, { segments = 7, amp = 24 } = {}) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;

  // Unit direction and its perpendicular
  const ux = dx / len,
    uy = dy / len;
  const px = -uy,
    py = ux;

  const xs = [],
    ys = [],
    times = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;

    // base point on the straight line
    const bx = from.x + dx * t;
    const by = from.y + dy * t;

    // alternate left/right; taper near the ends
    const sign = i % 2 === 0 ? 1 : -1;
    const taper = i === 0 || i === segments ? 0 : 1 - Math.abs(0.5 - t) * 1.6; // smaller at the ends
    const off = amp * sign * taper;

    xs.push(bx + px * off);
    ys.push(by + py * off);
    times.push(t);
  }
  return { xs, ys, times };
}

// --- Defeat (animated broken-heart + red sparkles) ---
function DefeatIcon({ size = 104 }) {
  const sparkle = (x, y, delay = 0) => (
    <motion.span
      key={`${x}-${y}-${delay}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: [0.25, 0.9, 0.25], scale: [0.85, 1.15, 0.85] }}
      transition={{ repeat: Infinity, duration: 1.6, delay }}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        fontSize: 18,
        color: '#ff6b6b',
        textShadow: '0 0 10px rgba(255,107,107,0.8), 0 0 2px #000',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      ✦
    </motion.span>
  );

  return (
    <div
      style={{
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        margin: '0px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, rotate: 2, opacity: 0 }}
        animate={{
          scale: 1,
          rotate: [2, -2, 2],
          opacity: 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 260,
          damping: 18,
          rotate: { repeat: Infinity, duration: 2.2 },
        }}
        style={{
          filter:
            'drop-shadow(0 8px 22px rgba(255,107,107,0.45)) drop-shadow(0 2px 8px rgba(0,0,0,0.55))',
        }}
      >
        <span
          role="img"
          aria-label="defeat"
          style={{ fontSize: size, lineHeight: 1, display: 'inline-block' }}
        >
          <img src="/v2/loss.png" style={{ height: '100px', marginBottom: '0px' }} />
        </span>
      </motion.div>

      {sparkle('22%', '10%', 0.1)}
      {sparkle('78%', '22%', 0.35)}
      {sparkle('12%', '64%', 0.7)}
      {sparkle('86%', '68%', 1.0)}
    </div>
  );
}

// --- Trophy (animated emoji + sparkles) ---
function TrophyIcon({ size = 104 }) {
  const sparkle = (x, y, delay = 0) => (
    <motion.span
      key={`${x}-${y}-${delay}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.25, 0.9] }}
      transition={{ repeat: Infinity, duration: 1.6, delay }}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        fontSize: 18,
        color: '#ffe15f',
        textShadow: '0 0 10px rgba(255,225,95,0.8), 0 0 2px #000',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      ✦
    </motion.span>
  );

  return (
    <div
      style={{
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        margin: '0px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, rotate: 2, opacity: 0 }}
        animate={{
          scale: 1,
          rotate: [2, -2, 2],
          opacity: 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 260,
          damping: 18,
          rotate: { repeat: Infinity, duration: 2.2 },
        }}
        style={{
          filter:
            'drop-shadow(0 8px 22px rgba(255,223,95,0.55)) drop-shadow(0 2px 8px rgba(0,0,0,0.55))',
        }}
      >
        <span
          role="img"
          aria-label="trophy"
          style={{ fontSize: size, lineHeight: 1, display: 'inline-block' }}
        >
          <img src="/v2/win.png" style={{ height: '100px', marginBottom: '0px' }} />
        </span>
      </motion.div>

      {sparkle('20%', '8%', 0.0)}
      {sparkle('80%', '18%', 0.3)}
      {sparkle('10%', '60%', 0.6)}
      {sparkle('86%', '66%', 0.9)}
    </div>
  );
}

/* ----------------------------------- UI bits ----------------------------------- */
function SearchingBadge({ elapsed = 0 }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: '6px 10px',
        borderRadius: 8,
        background: '#ffe18c',
        color: '#fff',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
      }}
      className="shadow1"
    >
      <span
        style={{
          width: 12,
          height: 12,
          display: 'inline-block',
          borderRadius: '50%',
          border: '2px solid rgba(0,0,0,0.7)',
          borderTopColor: 'transparent',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <motion.span
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ repeat: Infinity, duration: 1.4 }}
      >
        Searching Opponent... {formatCountdown(elapsed)}
      </motion.span>
    </div>
  );
}

function NoticeStack({ item, speed = 1 }) {
  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 110,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      <AnimatePresence initial={false}>
        {item && (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95, filter: 'blur(3px)' }}
            transition={{ duration: 0.4 * speed, ease: 'easeInOut' }}
            style={{
              color: item.color,
              fontSize: 18,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              fontWeight: 600,
              fontFamily: '"Fredoka", sans-serif',
            }}
            className="shadow2"
          >
            {/* visible text */}
            {item.text}

            {/* a11y live region */}
            <div
              aria-live="polite"
              style={{
                position: 'absolute',
                left: -9999,
                top: 'auto',
                width: 1,
                height: 1,
                overflow: 'hidden',
              }}
            >
              {item.text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FloaterLayer({ items, speed = 1 }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 4,
      }}
    >
      <AnimatePresence initial={false}>
        {items.map((f) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 0, y: f.y + 10, x: f.x, scale: 0.96 }}
            animate={{ opacity: 1, y: f.y - 22, x: f.x, scale: 1 }}
            exit={{ opacity: 0, y: f.y - 48, x: f.x, scale: 0.98 }}
            transition={{
              duration: Math.min(0.6, (f.duration ?? 0.9) * 0.66) * speed,
              ease: 'easeOut',
            }}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: `translate(${f.x}px, ${f.y}px)`,
              color: f.color || '#fff',
              fontSize: 20,
              fontWeight: 700,
              textShadow: '0 1px 2px rgba(0,0,0,0.8)',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            {f.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

const StatPill = ({ children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",   // vertical centering
      justifyContent: "center",
      gap: 3,
      padding: "2px 0px",
      fontSize: 16,
      lineHeight: 1,
      color: "#fff"
    }}
  >
    {children}
  </div>
);

function HealthBar({ label, hp, max, side = 'left' }) {
  const pct = clamp(Math.round((hp / (max || 1)) * 100), 0, 100);
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        [side]: 8,
        width: 'min(calc(50% - 14px), 483px)',
        maxWidth: 'calc(50% - 14px)',
        background: '#51352c',
        border: '1px solid #852801',
        borderRadius: 6,
        padding: 6,
        color: '#fff',
        backdropFilter: 'blur(2px)',
        boxSizing: 'border-box',
        zIndex: 2,
        fontFamily: '"Fredoka", sans-serif',
        fontWeight: 600
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 13,
          gap: 8,
        }}
      >
        <strong
          style={{
            textTransform: 'uppercase',
            fontWeight: 600,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 2px #000',
          }}
          title={label}
        >
          {label}
        </strong>
      </div>
      <div
        style={{
          padding: 3,
          boxSizing: "border-box",
          height: 21,
          marginTop: 6,
          borderRadius: 4,
          overflow: 'hidden',
          background: 'rgba(0,0,0,0.55)',
          position: "relative"
        }}
      >

        <div className="shadow1" style={{fontSize: "12px", position: "absolute",right: 6, top: 3}}>
          {hp} / {max}
        </div>

        <motion.div
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          style={{
            borderRadius: 4,
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(90deg,#FF3A35,#D80002)',
          }}
        />
      </div>
    </div>
  );
}

const Avatar = React.forwardRef(({ unit, side, glow, noBg = false, introActive = false }, ref) => {
  const layers = Array.isArray(unit?.characterLayers)
    ? noBg
      ? unit.characterLayers.filter((layer) => {
          if (typeof layer === 'string') {
            return !/\/background\//i.test(layer);
          }
          return layer?.part !== 'background' && !/\/background\//i.test(layer?.src || '');
        })
      : unit.characterLayers
    : [];

  const hasLayers = layers.length > 0;
  const boxWH = 'min(38vw, 140px)';

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: side === 'left' ? 'row' : 'row-reverse',
        alignItems: 'center',
        gap: 10,
        filter: glow ? 'drop-shadow(0 0 12px rgba(60,255,155,0.6))' : 'none',
        transition: 'filter 180ms ease',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: boxWH,
          height: boxWH,
          filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.8))',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            transform: side === 'right' ? 'scaleX(-1)' : 'none',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {hasLayers ? (
            layers.map((layer, i) => {
              const src = typeof layer === 'string' ? layer : layer?.src;
              const zIndex = typeof layer === 'string' ? i + 1 : layer?.zIndex ?? i + 1;

              return (
                <img
                  key={layer?.part || i}
                  src={src}
                  alt={layer?.part || `layer-${i}`}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex,
                  }}
                />
              );
            })
          ) : (
            <img
              src={unit.art || '/pack.png'}
              alt={unit.name}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                transform: side === 'right' ? 'scaleX(-1)' : 'none',
              }}
            />
          )}
        </div>

        {/* name */}
        <div
          style={{
            position: 'absolute',
            left: 4,
            right: 4,
            top: -36,
            padding: '6px 8px',
            borderRadius: 8,
            background: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 16,
            lineHeight: 1.1,
            textAlign: 'center',
            textShadow: '0 1px 2px #000',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            pointerEvents: 'none',
            fontFamily: '"Fredoka", sans-serif',
            fontWeight: 600
          }}
          title={unit.name}
        >
          {unit.name}
        </div>

        {/* stats */}
        <div
          style={{
            position: 'absolute',
            left: 4,
            right: 4,
            bottom: introActive ? -32 : -21,
            padding: 4,
            borderRadius: 10,
            background: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            display: 'none',
            gap: 6,
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          {(() => {
            const showBase = side === 'left' && unit?.base; // left = player

            const dispAtk = showBase
              ? unit.base.attack
              : Number(unit.visualAttack ?? unit.displayStats?.attack ?? unit.attack);

            const dispDef = showBase
              ? unit.base.defense
              : Number(unit.visualDefense ?? unit.displayStats?.defense ?? unit.defense);

            const dispHp = showBase
              ? unit.base.maxHp
              : Number(unit.visualHealth ?? unit.displayStats?.health ?? unit.maxHp);

            return (
              <>
                <StatPill><img src="/s_attack.png" height="14"/> {dispAtk}</StatPill>
                <StatPill><img src="/s_defense.png" height="14"/> {dispDef}</StatPill>
                <StatPill><img src="/s_health.png" height="14"/> {dispHp}</StatPill>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
});

function svgFallback(name = "?") {
  const initial = (name?.[0] || "?").toUpperCase();

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
      <rect width="100%" height="100%" fill="#914422"/>
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        font-size="32"
        fill="#fff"
        font-family="Arial, sans-serif"
      >
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}


function BattleSplashCard({ unit, user, side = 'left', opponent = false, safeTop = false }) {
  if (!unit) return null;

  const attack = Number(unit?.visualAttack ?? unit?.displayStats?.attack ?? unit?.attack ?? 0);
  const defense = Number(unit?.visualDefense ?? unit?.displayStats?.defense ?? unit?.defense ?? 0);
  const health = Number(unit?.visualHealth ?? unit?.displayStats?.health ?? unit?.maxHp ?? 0);

const level = (() => {
  const real =
    unit?.level ??
    unit?.cardLevel ??
    unit?.character?.level ??
    unit?.serverMeta?.level ??
    null;

  if (real !== null && real !== undefined) {
    return Number(real);
  }

  if (!opponent) return 1;

  const source = String(unit?.serverMeta?.source || '').toLowerCase();

  if (source === 'live_pvp' || source === 'defense_deck') {
    return 1;
  }

  return randInt(1, 10);
})();

const wins = (() => {
  const real =
    unit?.wins ??
    unit?.battleWins ??
    unit?.serverMeta?.wins ??
    unit?.serverMeta?.battleWins ??
    null;

  if (real !== null && real !== undefined) {
    return Number(real);
  }

  if (!opponent) return 0;

  const source = String(unit?.serverMeta?.source || '').toLowerCase();

  if (source === 'live_pvp' || source === 'defense_deck') {
    return 0;
  }

  const seedText = String(unit?.id || unit?.name || 'ai');
  let seed = 0;

  for (let i = 0; i < seedText.length; i += 1) {
    seed += seedText.charCodeAt(i);
  }

  return 5 + (seed % 95);
})();

  
const opponentSource = String(
  unit?.serverMeta?.source || ''
)
  .trim()
  .toLowerCase();

/*
 * Somente "ai" é TAPTOPIA.
 *
 * live_pvp     = usuário real
 * defense_deck = usuário real
 * challenge    = usuário real
 */
const isAiOpponent =
  opponent && opponentSource === 'ai';


/*
 * PERFIL
 *
 * Player local:
 * usa o mesmo `user` do GamesTopBar.
 *
 * Opponent humano:
 * vem do backend em serverMeta.profile.
 *
 * AI:
 * não tem perfil/cosméticos.
 */
const profileUser = isAiOpponent
  ? {}
  : opponent
    ? unit?.serverMeta?.profile || {}
    : user || {};


/*
 * MESMA prioridade do GamesTopBar:
 *
 * name
 * username
 * fallback
 */
const playerName = isAiOpponent
  ? 'TAPTOPIA'
  : (
      profileUser?.name ||
      profileUser?.username ||
      'Player'
    );

const cardName =
  unit?.name ||
  'Card';


/*
 * MESMA lógica de cosmetics do GamesTopBar.
 */
const {
  bannerSrc,
  emojiSrc,
  nameStyle,
} = isAiOpponent
  ? {
      bannerSrc: null,
      emojiSrc: null,
      nameStyle: {},
    }
  : getUserNameCosmetics(
      profileUser
    );


/*
 * IMPORTANTE:
 *
 * GamesTopBar NÃO mostra bg_style_1
 * quando o usuário não tem banner.
 */
const bannerStyle = bannerSrc
  ? getUserBannerStyle(
      profileUser
    )
  : {
      backgroundColor: '#673a27',
      backgroundImage: 'none',
    };


const playerAvatar = isAiOpponent
  ? null
  : (
      profileUser?.avatarUrl ||
      profileUser?.avatar_url ||
      profileUser?.photo_url ||
      profileUser?.avatar ||
      svgFallback(
        playerName
      )
    );

  
  const layers = Array.isArray(unit?.characterLayers) ? unit.characterLayers : [];
  const hasLayers = layers.length > 0;

  const statRow = {
    display: 'grid',
    gridTemplateColumns: '15px 1fr auto',
    alignItems: 'center',
    gap: 3,
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    textShadow: '0 2px 0 #351405, 0 1px 2px #000',
    whiteSpace: 'nowrap',
  };

  const statIcon = {
    width: 16,
    height: 16,
    objectFit: 'contain',
    filter: 'drop-shadow(0 2px 0 rgba(0,0,0,0.45))',
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      style={{
        width: 300,
        height: 215,
        position: 'relative',
        marginTop: safeTop ? 'calc(var(--tg-safe-top, 0px) + 10px)' : 0,
        boxSizing: 'border-box',
        borderRadius: 12,
        border: '1px solid #562006',
        background: 'linear-gradient(180deg, #f5b33b 0%, #8b3f10 100%)',
        boxShadow:
          'inset 0 0 0 2px rgba(255,221,91,.75), 0 3px 0 rgba(54,18,4,.95), 0 4px 4px rgba(0,0,0,.28)',
        overflow: 'hidden',
        fontFamily: '"Fredoka", sans-serif',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 6,
          borderRadius: 8,
          background: '#563018',
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: 10,
          right: 10,
          top: 8,
          height: 42,
          zIndex: 6,
          display: 'flex',
alignItems: 'center',
justifyContent: 'center',
gap: 3,
          padding: '0 2px',
          boxSizing: 'border-box',
          color: '#fff',
          fontSize: 18,
          fontWeight: 600,
          textShadow: '0 2px 0 #321204, 0 1px 2px #000',
          background: 'linear-gradient(180deg, #9a612d, #6f3d1d)',
border: '2px solid #5b2709',
borderRadius: 8,
whiteSpace: 'nowrap',
overflow: 'hidden',
justifyContent: 'flex-start',
textOverflow: 'ellipsis',
...bannerStyle,
        }}
      >
      
       {playerAvatar && (
  <div
    style={{
      flex: '0 0 30px',
      width: 30,
      height: 30,
      background: '#000',
      borderRadius: 5,
      overflow: 'hidden',
    }}
  >
    <img
      src={playerAvatar}
      alt=""
      draggable={false}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 5,
      }}
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src =
          svgFallback(
            playerName || 'Player'
          );
      }}
    />
  </div>
)}

{emojiSrc && (
  <div
    style={{
      flex: '0 0 30px',
      width: 30,
      height: 30,
      background: '#2f170b',
      borderRadius: 5,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <img
      src={emojiSrc}
      alt=""
      draggable={false}
      style={{
        display: 'block',
        width: 24,
        height: 24,
        objectFit: 'contain',
      }}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  </div>
)}
        <div
  style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minWidth: 0,
    lineHeight: 1.2,
  }}
>
<div
  style={{
    width: '100%',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '18px',
    textTransform: 'uppercase',
    fontFamily: '"Fredoka", sans-serif',
  }}
>
  <span
    style={{
      display: 'inline-block',
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',

      color: '#fff',
      textShadow: 'none',
      filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.75))',

      ...nameStyle,
    }}
  >
    {playerName}
  </span>
</div>

  <div
  style={{
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontWeight: 600,
    opacity: 0.9,
    color: '#f2d94b',
  }}
>
 {cardName}
</div>
</div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 10,
          top: 54,
          width: "50%",
          aspectRatio: 1,
          borderRadius: 8,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #e5a1ad 0%, #f3beb2 100%)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            transform: side === 'right' ? 'scaleX(-1)' : 'none',
          }}
        >
          {hasLayers ? (
            layers.map((layer, i) => {
              const src = typeof layer === 'string' ? layer : layer?.src;
              const zIndex = typeof layer === 'string' ? i + 1 : layer?.zIndex ?? i + 1;

              return (
                <img
                  key={layer?.part || i}
                  src={src}
                  alt=""
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex,
                  }}
                />
              );
            })
          ) : (
            <img
              src={unit.art || '/pack.png'}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          )}
        </div>

        <div
          style={{
            position: 'absolute',
            left: 6,
            top: 6,
            width: 28,
            height: 28,
            borderRadius: '50%',
            zIndex: 5,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 18,
            fontWeight: 600,
            background: 'linear-gradient(180deg, #743314, #4a1c08)',
            border: '3px solid #f6c24a',
            textShadow: '0 2px 0 #000',
            boxShadow: '0 3px 0 rgba(0,0,0,0.42)',
          }}
        >
          {level}
        </div>

        <div
          style={{
            position: 'absolute',
            left: -1,
            right: 0,
            bottom: 0,
            height: 28,
            lineHeight: "28px",
            zIndex: 4,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            textShadow: '0 2px 0 #351405, 0 1px 2px #000',
            background: "#816036"
          }}
        >
          {unit.rarity || 'Common'}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: "58%",
          right: 20,
          top: opponent ? 62 : 62,
          bottom: 14,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: opponent ? 8 : 8,
        }}
      >
        <div
  style={{
    textAlign: 'center',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    textShadow: '0 2px 0 #351405, 0 1px 2px #000',
    marginBottom: 2,
  }}
>
  <div>Wins</div>

  <div style={{ fontSize: 16, margin: '4px 0 2px' }}>
    <img
      src="/v2/battle.png"
      alt=""
      style={{ height: 24 }}
    />
  </div>

  <div style={{ color: '#ffe957', fontSize: 15 }}>
    {wins}
  </div>
</div>

        <div style={statRow}>
          <img src="/s_attack.png" alt="" style={statIcon} />
          <span>Attack</span>
          <strong style={{ color: '#ffe957' }}>{attack}</strong>
        </div>

        <div style={statRow}>
          <img src="/s_defense.png" alt="" style={statIcon} />
          <span>Defense</span>
          <strong style={{ color: '#ffe957' }}>{defense}</strong>
        </div>

        <div style={statRow}>
          <img src="/s_health.png" alt="" style={statIcon} />
          <span>Health</span>
          <strong style={{ color: '#ffe957' }}>{health}</strong>
        </div>
      </div>
    </motion.div>
  );
}


/* --------------------------- Ability icons (optional pulse) --------------------------- */
function fileSafeAbility(name) {
  return toId(name).replace(/\s+/g, '_');
}
const ABIL_ICON_PATH = '';

function BattleStatRow({ unit }) {
  const attack = Number(unit?.visualAttack ?? unit?.displayStats?.attack ?? unit?.attack ?? 0);
  const defense = Number(unit?.visualDefense ?? unit?.displayStats?.defense ?? unit?.defense ?? 0);
  const health = Number(unit?.visualHealth ?? unit?.displayStats?.health ?? unit?.maxHp ?? 0);

  const statStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    color: '#ffe957',
    fontFamily: '"Fredoka", sans-serif',
    fontSize: 16,
    lineHeight: 1,
    textShadow: '0 2px 0 #5a2508, 0 1px 2px #000',
    whiteSpace: 'nowrap',
    minWidth: 34,
    fontWeight: 600,
    paddingTop: 3

  };

  const iconStyle = {
    width: 18,
    height: 18,
    objectFit: 'contain',
    filter: 'drop-shadow(0 2px 0 rgba(0,0,0,0.45))',
  };

  return (
    <div
      style={{
        gridColumn: '1 / -1',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        width: '100%',
        marginBottom: 2,
      }}
    >
      <div style={statStyle}>
        <img src="/s_attack.png" alt="Attack" style={iconStyle} />
        {attack}
      </div>

      <div style={statStyle}>
        <img src="/s_defense.png" alt="Defense" style={iconStyle} />
        {defense}
      </div>

      <div style={statStyle}>
        <img src="/s_health.png" alt="Health" style={iconStyle} />
        {health}
      </div>
    </div>
  );
}

function AbilityIcon({ name, active, pulse, srcOverride, empty = false, title }) {
  const src = srcOverride || (name ? `${ABIL_ICON_PATH}/a_${fileSafeAbility(name)}.png` : null);

  return (
    <motion.div
      title={title || name || ''}
      animate={pulse ? { scale: [1, 1.12, 1] } : { scale: 1 }}
      transition={{ duration: 0.45 }}
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        display: 'grid',
        placeItems: 'center',
        justifySelf: 'center',
        alignSelf: 'center',

        background: empty
          ? 'linear-gradient(180deg, rgba(50,20,10,0.95), rgba(25,10,5,0.98))'
          : active
          ? 'linear-gradient(180deg, #ffe86a 0%, #f2b734 55%, #bd641e 100%)'
          : 'linear-gradient(180deg, rgba(68,27,12,0.95), rgba(32,13,7,0.98))',

        border: active ? '2px solid #fff06a' : '2px solid #9b5a24',

        boxShadow: active
          ? `
            inset 0 0 0 1px rgba(98,36,9,0.65),
            0 2px 0 rgba(35,12,4,0.95),
            0 0 10px rgba(255,220,70,0.65)
          `
          : `
            inset 0 0 0 1px rgba(30,10,4,0.75),
            0 2px 0 rgba(35,12,4,0.95)
          `,

        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={title || name || 'icon'}
          style={{
            width: 25,
            height: 25,
            objectFit: 'contain',
            filter: active
              ? 'drop-shadow(0 2px 0 rgba(0,0,0,0.3))'
              : 'drop-shadow(0 2px 0 rgba(0,0,0,0.35))',
            opacity: empty ? 0.35 : 1,
          }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
    </motion.div>
  );
}

function useAbilityFlash(getDelayMs = (ms) => ms) {
  const [flashMap, setFlashMap] = useState({}); // { [unitId]: Set<abilityId> }

  const flashDelay = (ms) => getDelayMs(ms);

  const flashAbility = (unitId, abilityName, ms = 800) => {
    const id = toId(abilityName);
    setFlashMap((m) => {
      const set = new Set(m[unitId] || []);
      set.add(id);
      return { ...m, [unitId]: set };
    });

    setTimeout(() => {
      setFlashMap((m) => {
        const set = new Set(m[unitId] || []);
        set.delete(id);
        return { ...m, [unitId]: set };
      });
    }, flashDelay(ms));
  };

  return { flashMap, flashAbility, flashDelay };
}

function AbilityLegend({
  unit,
  side = 'left',
  flashes,
  children,
  shieldEnabled = false,
  shieldSecondsLeft = 0,
}) {
  const abilitySlots = React.useMemo(() => {
    const names = Array.isArray(unit?.abilities) ? unit.abilities.filter(Boolean) : [];
    return Array.from({ length: 3 }, (_, index) => names[index] || null);
  }, [unit?.abilities]);

  const isLeft = side === 'left';

  return (
    <div
      style={{
        position: 'absolute',
        bottom: -10,

        left: isLeft ? 4 : 'calc(50% + 6px)',
        right: isLeft ? 'calc(50% + 6px)' : 4,

        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,

        padding: '5px 5px 7px',
        borderRadius: 12,

        background: 'linear-gradient(180deg, rgba(91,39,17,0.88), rgba(42,17,8,0.94))',
        border: '2px solid #8f4219',

        boxShadow: `
          inset 0 0 0 1px rgba(255,155,57,0.22),
          inset 0 -5px 8px rgba(0,0,0,0.22),
          0 3px 0 rgba(44,16,7,0.9)
        `,

        overflow: 'hidden',
        zIndex: 2,
        boxSizing: 'border-box',
        pointerEvents: 'none',
      }}
    >
      <BattleStatRow unit={unit} />

      {abilitySlots.map((name, index) => {
        if (!name) {
          return <AbilityIcon key={`empty-ability-${index}`} empty />;
        }

        const id = toId(name);
        const pulse = !!flashes?.has?.(id);

        return (
          <AbilityIcon
            key={`${name}-${index}`}
            name={name}
            active={pulse}
            pulse={pulse}
          />
        );
      })}

      {shieldEnabled ? (
        <AbilityIcon
          key="shield-slot"
          name="Shield"
          srcOverride="/a_aegis.png"
          active
          pulse={false}
          title={`Shield Active - ${formatCountdown(shieldSecondsLeft)} remaining`}
        />
      ) : (
        <AbilityIcon key="empty-shield-slot" empty />
      )}

      {children ? (
        <div
          style={{
            gridColumn: '1 / -1',
            marginTop: 4,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------- Character helpers ---------------------------------- */
function characterLayersFrom(card) {
  if (!card || !card.character) return [];

  const order = [
    'background',
    'body',
    'boots',
    'pants',
    'shirt',
    'neck',
    'face',
    'eyes',
    'mouth',
    'head',
    'weapons',
  ];

  const zIndexMap = {
    background: 1,
    body: 2,
    boots: 3,
    pants: 4,
    shirt: 5,
    neck: 6,
    face: 7,
    eyes: 8,
    mouth: 9,
    head: 10,
    weapons: 11,
  };

  const entries = Object.entries(card.character || {});

  const seasonName =
    card?.seasonal_accessory?.season_name ||
    card?.season_name ||
    null;

  return entries
    .sort(([a], [b]) => {
      const indexA = order.includes(a) ? order.indexOf(a) : order.length;
      const indexB = order.includes(b) ? order.indexOf(b) : order.length;
      return indexA - indexB;
    })
    .map(([part, data]) => {
      if (!data || typeof data !== 'object') return null;

      const defaultSrc =
        `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

      return {
        part,
        src: getSkinSrc(seasonName, part, defaultSrc),
        zIndex: zIndexMap[part] ?? 10,
      };
    })
    .filter(Boolean);
}

/* ---------------------------------- Damage model ---------------------------------- */
function computeDamage(attacker, defender) {
  const defRaw = defender.defense;
  const defUsed = has(attacker, 'pierce') ? Math.floor(defRaw * 0.7) : defRaw;
  const mit = defUsed / (defUsed + K_DEF_MIT);
  const base = Math.round(attacker.attack * (1 - mit));
  const unclamped = Math.max(1, base);
  const maxChunk = Math.ceil(defender.maxHp * MAX_CHUNK_PCT);
  return Math.min(unclamped, maxChunk);
}

function PrizesWonModal({ open, prizes, onClose }) {
  const list = Array.isArray(prizes) ? prizes.filter(Boolean) : prizes ? [prizes] : [];
  const primary = list[0] || null;

  // tenta inferir amount + label no estilo do print
  const amount = primary?.amount ?? primary?.qty ?? primary?.quantity ?? null;

  const rawName = String(primary?.name ?? primary?.itemName ?? primary?.label ?? primary?.type ?? 'Reward');
  const lower = rawName.toLowerCase();

  // tenta padronizar "Sparks" / "Runes"
  const currencyLabel =
    lower.includes('spark') ? 'Sparks' :
    lower.includes('rune') ? 'Runes' :
    null;

  const titleText =
    amount != null && currencyLabel
      ? `${amount} ${currencyLabel} Prize`
      : amount != null
      ? `${amount} ${rawName}`
      : rawName;

  // imagem grande central (no print fica no topo)
  const img = primary ? getRewardIcon(primary) : null;

  // opcional: se teu backend manda trophies/milestone etc
  const unlockedAt =
    primary?.unlockedAt ??
    primary?.trophiesRequired ??
    primary?.requiredTrophies ??
    null;

  return (
    <AnimatePresence>
      {open && (
        <div
          className="modal_box"
        >

 <div className="modal_title">
 You Got A New Item
            <button
              onClick={onClose}
              aria-label="Close"
              className="modal_close"
            >
            </button>
    </div>


          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
           
            <div className="modal_inside">
              
              
                  {img ? (
                    <img
                      src={img}
                      alt={titleText}
                      style={{ height: 100 }}
                    />
                  ) : (
                    <span style={{ marginBottom: -10, fontSize: 64 }}>🎁</span>
                  )}
              

              {/* BIG TITLE */}
              <div
                style={{
                  fontSize: 42,
                  fontWeight: 600,
                  lineHeight: '68px',
                  color: '#fff',
                }}
                className="shadow2"
              >
                {titleText}
              </div>

              <div className="info">
              <div
                style={{
                  display: 'block',
                  fontSize: 18,
                  color: '#fff',
                  textShadow: '3px 3px 0 #2b1400',
                  letterSpacing: 1,
                }}
              >
                Added To Your Inventory!
              </div>

              {/* SMALL LINE */}
              {unlockedAt != null && (
                <div
                  style={{
                    marginTop: 6,
                    fontWeight: 300,
                    color: 'rgba(255,255,255,0.85)',
                    textShadow: '0 2px 0 rgba(0,0,0,0.65)',
                    fontSize: 21,
                    fontWeight: 600
                  }}
                >
                  Unlocked At {unlockedAt} Trophies
                </div>
              )}

              {list.length > 1 && (
                <div
                  style={{
                    marginTop: 14,
                    width: 'min(92vw, 560px)',
                    padding: 12,
                    borderRadius: 16,
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    display: 'grid',
                    gap: 8,
                  }}
                >
                  {list.slice(0, 4).map((r, idx) => {
                    const t = String(r?.name ?? r?.itemName ?? r?.label ?? r?.type ?? 'Reward');
                    const a = r?.amount ?? r?.qty ?? r?.quantity ?? null;
                    return (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 10,
                          fontWeight: 900,
                          color: '#fff',
                          textShadow: '0 2px 0 rgba(0,0,0,0.65)',
                        }}
                      >
                        <span style={{ opacity: 0.95 }}>{t}</span>
                        {a != null ? <span style={{ color: '#ffdf5f' }}>+{a}</span> : null}
                      </div>
                    );
                  })}

                </div>
              )}

              </div>
              </div>

              {/* COLLECT */}
              <div className="modal_footer">
                <button
                  onClick={onClose}
                 className="game-button green-button"
                >
                  Awesome
                </button>
              </div>

         </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function ArenaCompletedModal({ open, info, onClose, onGoNextArena, onGoArenaHub }) {
  const nextArenaId = info?.nextArenaId ?? info?.toArenaId ?? null;

  // ✅ regra: se backend disser allCompleted OU não existir nextArenaId -> acabou tudo
  const allDone = !!info?.allCompleted || !nextArenaId;

  return (
    <AnimatePresence>
      {open && (
        <div
          className="modal_box"
        >

          <div className="modal_title">Congratulations
          <div className="modal_close" onClick={() => {
                    if (allDone) onGoArenaHub?.();
                    else onGoNextArena?.(nextArenaId);
                  }}/>
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

            <div className="modal_inside">
              
                  <img
                    src="/won.png"
                    alt="Arena Completed"
                    style={{ height: 100, marginBottom: -10,marginTop: -10 }}
                  />

              <div
                style={{
                  fontWeight: 600,
                  fontSize: "32px",
                  lineHeight: "64px",
                  color: "#fff",
                }}

                className="shadow2"
              >
                {allDone ? "All Arenas Completed" : "Arena Completed"}
              </div>

              <div
                className="info"
                style={{
                  fontSize: 21,
                  fontWeight: 600,
                  color: allDone ? "#ffdf5f" : "#ffdf5f",
                }}
              >
                {allDone ? "Awesome!" : "New Arena Unlocked"}
              </div>
              </div>

              {/* Buttons */}
              <div className="modal_footer">
                <button
                  onClick={() => {
                    if (allDone) onGoArenaHub?.();
                    else onGoNextArena?.(nextArenaId);
                  }}
                  className="game-button green-button"
                >
                  {allDone ? "Back To Arenas" : "Go To Next Arena"}
                </button>
              
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function ChestWonModal({ open, chest, onClose }) {
  const chestLabel = chest?.label ?? "Chest";
  const chestImage = chest?.image ? `/${chest.image}` : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="modal_box"
        >
          <div className="modal_title">
            You Got A New Chest
             <button
              onClick={onClose}
              aria-label="Close"
              className="modal_close"
            >
            </button>
          </div>

          <div className="modal_body">

            <div className="modal_inside">
              {/* Chest art */}
              {chestImage && (
                  <img src={chestImage} style={{height: 120,marginTop: -20, marginBottom: -20}}/>
              )}

              <div
                style={{
                      fontWeight: 600,
                  fontSize: "32px",
                  lineHeight: "62px",
                  color: "#fff",
                }}
                className="shadow2"
              >
                {chestLabel} CHEST
              </div>
  </div>

              {/* Collect */}
              <div className="modal_footer">
                <button onClick={onClose} className="game-button green-button">
                  Awesome!
                </button>
              </div>
            
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* =================================== MAIN =================================== */
export default function Battle({
  user,
  setUser,

  selectedCardId,

  onNavigate,

  battleId:
    battleIdProp,

  cardId:
    cardIdProp,

  sfxEnabled,

  sfxVolume,

  openedVia,

  returnTo =
    "fight",

  returnParams =
    {},

  arenaId:
    arenaIdProp,

  challengeId:
    challengeIdProp = null,

  tournamentId:
    tournamentIdProp = null,

  tournamentMatchId:
    tournamentMatchIdProp = null,

  initialOpponent =
    null,

  initialPlaySig =
    null,

}) {

const debugBattleMode = false;

const [
  activeChallengeId,
  setActiveChallengeId
] = useState(
  () =>
    challengeIdProp ||
    null
);

  const [
  activeTournamentId,
  setActiveTournamentId
] = useState(
  () =>
    tournamentIdProp ||
    null
);

const [
  activeTournamentMatchId,
  setActiveTournamentMatchId
] = useState(
  () =>
    tournamentMatchIdProp ||
    null
);

  const [p1, setP1] = useState(null);
  const [p2, setP2] = useState(null);
  const [introPlayer, setIntroPlayer] = useState(null);

                const effectiveReturnTo =
  activeTournamentId
    ? (returnTo || "tournamentsV2")
    : activeChallengeId
      ? "challenges"
      : (
          returnTo ||
          (cardIdProp ? "fight" : "arenas")
        );

const effectiveReturnParams =
  activeTournamentId
    ? {
        ...(returnParams || {}),
        tournamentId: activeTournamentId,
        initialView:
          returnParams?.initialView ||
          "battles",
      }
    : activeChallengeId
      ? {
          ...(returnParams || {}),
          initialChallengeId:
            activeChallengeId,
        }
      : (
          returnParams ||
          {}
        );

const effectiveArenaId =
  activeChallengeId ||
  activeTournamentId
    ? null
    : (
        effectiveReturnParams?.arenaId ??
        arenaIdProp ??
        null
      );

const isTournamentBattle =
  !!activeTournamentId;

const isChallengeBattle =
  !!activeChallengeId;

const isSpecialBattle =
  isChallengeBattle ||
  isTournamentBattle;

const isArenaBattle =
  !isSpecialBattle &&
  (
    !!effectiveArenaId ||
    effectiveReturnTo === "arenas"
  );

  const [limitInfo, setLimitInfo] = useState(null);
  const limitReached = !!limitInfo && limitInfo.used >= limitInfo.limit;

  const [modalQueue, setModalQueue] = useState([]);
  const enqueueModal = useCallback((type, payload) => {
    setModalQueue((q) => [...q, { type, payload }]);
  }, []);
  const closeActiveModal = useCallback(() => {
    setModalQueue((q) => q.slice(1));
  }, []);

  const activeModal = modalQueue[0] || null;

  const [
  playSig,
  setPlaySig
] = useState(
  () =>
    initialPlaySig ||
    null
);

  const [notice, setNotice] = useState(null);
  const noticeQueueRef = useRef([]);
  const showingRef = useRef(false);

  const [isPaused, setIsPaused] = useState(false);

const [oppStatus, setOppStatus] =
  useState(
    () =>
      (
        challengeIdProp ||
        tournamentIdProp
      ) &&
      initialOpponent
        ? "found"
        : "searching"
  );
  
  const [introActive, setIntroActive] = useState(true);
  const initialTurnRef = useRef(1); // who starts once intro ends
  const [searchElapsed, setSearchElapsed] = useState(0);
  const [isSkippingMatchmaking, setIsSkippingMatchmaking] = useState(false);
  const [skipButtonText, setSkipButtonText] = useState('Skip Matchmaking');

  const matchmakingCancelledRef = useRef(false);
  const matchmakingAbortRef = useRef(null);

  const [cooldownLeft, setCooldownLeft] = useState(0); // seconds remaining
  const [isStartingRematch, setIsStartingRematch] = useState(false);
  const [isActing, setIsActing] = useState(false);

  const [turn, setTurn] = useState(0); // 0=not started, 1=p1, 2=p2
  const [winner, setWinner] = useState(null);
  const [serverWinner, setServerWinner] = useState(null);
  const forcedWinnerRef = useRef(null);
  useEffect(() => {
    forcedWinnerRef.current = serverWinner || null;
  }, [serverWinner]);
  const [speed, setSpeed] = useState(1); // 1x, 2x, 3x
  const [skipToEnd, setSkipToEnd] = useState(false); // GO TO END

  const [awardedRewards, setAwardedRewards] = useState([]);
  const [streakReward, setStreakReward] = useState(null);
  const [battleStreak, setBattleStreak] = useState(null); // estado real vindo do /streak
  const [showAdRewardButton, setShowAdRewardButton] = useState(false);
  const [adRewardClaimed, setAdRewardClaimed] = useState(false);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const adControllerRef = useRef(null);

  // Loss-refund ad: 35% chance offer to refund entry fee after a defeat
  const [showLossRefundButton, setShowLossRefundButton] = useState(false);
  const [lossRefundPercent, setLossRefundPercent] = useState(0.35);
  const [lossRefundClaimed, setLossRefundClaimed] = useState(false);
  const lossRefundAdControllerRef = useRef(null);

  const [shieldLossProtected, setShieldLossProtected] = useState(false);
  const [shieldRefundedAmount, setShieldRefundedAmount] = useState(0);
  const [shieldSecondsLeft, setShieldSecondsLeft] = useState(0);

  // visuals
  const [projectile, setProjectile] = useState(null);
  const [impact, setImpact] = useState(null); // { x, y, type, id }

  const [floaters, setFloaters] = useState([]);
  const [log, setLog] = useState([]);

  const isPausedRef = useRef(isPaused);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // stats
  const statsRef = useRef({ turns: 0, p1Damage: 0, p2Damage: 0, abilities: 0 });

  // Refs
  const busyRef = useRef(false);
  const statusRef = useRef({}); // { [id]: { burn, bleed, aegisCharges, regenCharges } }
  const appliedOnceRef = useRef({}); // { [id]: { flameburstApplied: boolean, bleedApplied: boolean } }
  const arenaRef = useRef(null);
  const p1Ref = useRef(null);
  const p2Ref = useRef(null);
  const { flashMap, flashAbility, flashDelay } = useAbilityFlash(
    (ms) => animDur((ms / 1000) * TURN_SPEED) * 1000,
  );

  function waitIfPaused() {
    return new Promise((resolve) => {
      if (!isPausedRef.current) return resolve();
      const check = setInterval(() => {
        if (!isPausedRef.current) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  const introBg = user?.side === 'good' ? '/bbg2.png' : '/bbg1.png';
  const BATTLE_BG_COUNT = 8;
  const BG_URLS = React.useMemo(
    () => Array.from({ length: BATTLE_BG_COUNT }, (_, i) => `/battle_bg_${i + 1}.png`),
    [],
  );
  const randomBattleBg = () => BG_URLS[randInt(0, BG_URLS.length - 1)];
  const [arenaBg, setArenaBg] = useState(randomBattleBg());
  usePreloadImages(BG_URLS);

  const [abilityLabelP1, setAbilityLabelP1] = useState(null);
  const [abilityLabelP2, setAbilityLabelP2] = useState(null);

  // Wrapper to flash ability + show temporary text label
  function flashAbilityWithLabel(unitId, abilityName, ms = 800) {
    if (!unitId || !abilityName) return;

    const abilityId = toId(abilityName);
    const u = unitId === p1?.id ? p1 : unitId === p2?.id ? p2 : null;

    // ✅ only flash if this unit actually has the ability
    if (!u || !u.abilitiesSet?.has(abilityId)) return;

    flashAbility(unitId, abilityName, ms);

    const label = String(abilityName);
    const timeout = flashDelay(ms);

    if (unitId === p1?.id) {
      setAbilityLabelP1(label);
      setTimeout(() => {
        setAbilityLabelP1((prev) => (prev === label ? null : prev));
      }, timeout);
    }

    if (unitId === p2?.id) {
      setAbilityLabelP2(label);
      setTimeout(() => {
        setAbilityLabelP2((prev) => (prev === label ? null : prev));
      }, timeout);
    }
  }

  const handleCooldown = (data) => {
    const retryIn = Math.max(0, Number(data?.retryIn ?? 0));

    // store "until" so it survives UI re-renders (and optionally refresh)
    const until = Date.now() + retryIn * 1000;
    localStorage.setItem('battleCooldownUntil', String(until));

    setCooldownLeft(retryIn);

    const funny =
      data?.message ||
      (retryIn > 0
        ? `Whoa there 🐎 — wait ${retryIn}s and try again!`
        : `Whoa there 🐎 — slow down!`);

    pushLog(funny);
    const id = Math.random().toString(36).slice(2);
    enqueueNotice({ id, text: funny, color: '#ffdf5f', duration: 2.0 });
  };

  useEffect(() => {
    if (cooldownLeft <= 0) return;

    const t = setInterval(() => {
      const until = Number(localStorage.getItem('battleCooldownUntil') || 0);
      const left = Math.ceil((until - Date.now()) / 1000);
      setCooldownLeft(Math.max(0, left));
      if (left <= 0) {
        localStorage.removeItem('battleCooldownUntil');
      }
    }, 250);

    return () => clearInterval(t);
  }, [cooldownLeft]);

  const [battleScript, setBattleScript] = useState(null);
  const [scriptIndex, setScriptIndex] = useState(0);

  const resultPostedRef = useRef(false);

  const settlementPostedRef = useRef(false);

  const [battleId, setBattleId] = useState(() => battleIdProp || null);
  const [
  pendingOpponent,
  setPendingOpponent
] = useState(
  () =>
    initialOpponent ||
    null
);

        useEffect(() => {

  if (
    !initialOpponent
  ) {
    return;
  }

  const sm =
    initialOpponent
      ?.serverMeta;

  setBattleScript(
    sm?.script ||
    null
  );

  setScriptIndex(
    0
  );

  if (
    sm?.winner ===
      "player" ||

    sm?.winner ===
      "opponent"
  ) {
    setServerWinner(
      sm.winner ===
        "player"
        ? 1
        : 2
    );
  }

}, [
  initialOpponent
]);

  useEffect(() => {
    settlementPostedRef.current = false;
  }, [battleId, playSig]);

  useEffect(() => {
  setAwardedRewards([]);
  setShowAdRewardButton(false);
  setAdRewardClaimed(false);
  setIsWatchingAd(false);
  setShowLossRefundButton(false);
  setLossRefundClaimed(false);
  setShieldLossProtected(false);
  setShieldRefundedAmount(0);
}, [battleId]);

  useEffect(() => {
    if (introActive) {
      setSpeed(1);
      setSkipToEnd(false);
      setIsPaused(false);
    }
  }, [introActive, battleId]);

        useEffect(() => {
  const expiresAt = user?.shield_expires_at;

  if (!expiresAt) {
    setShieldSecondsLeft(0);
    return;
  }

  const tick = () => {
    setShieldSecondsLeft(getSecondsLeft(expiresAt));
  };

  tick();

  const intervalId = setInterval(tick, 1000);

  return () => clearInterval(intervalId);
}, [user?.shield_expires_at]);

  // If we didn't get a battleId via navigation (e.g., page reload), ask backend for the active pending session
  useEffect(() => {
    if (battleId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/active`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const d = await r.json().catch(() => ({}));
        if (!cancelled && r.ok && d?.battleId) {
  let activeData = d;

  if (!activeData?.opponent) {
    activeData = await waitForBattleReady(activeData, {
  cardId: cardIdProp || selectedCardId || localStorage.getItem('selectedCardId'),
  arenaId: effectiveArenaId,
});
  }

  setBattleId(activeData.battleId);
  setPlaySig(activeData.playSig || null);

            if (
  activeData
    ?.challengeId
) {
  setActiveChallengeId(
    String(
      activeData
        .challengeId
    )
  );
}

if (
  activeData?.tournamentId
) {
  setActiveTournamentId(
    String(
      activeData.tournamentId
    )
  );
}

if (
  activeData?.tournamentMatchId
) {
  setActiveTournamentMatchId(
    String(
      activeData.tournamentMatchId
    )
  );
}

  if (activeData?.opponent) {
    setPendingOpponent(activeData.opponent);

    const sm = activeData.opponent?.serverMeta;
    setBattleScript(sm?.script || activeData.script || null);
    setScriptIndex(0);

    if (sm && (sm.winner === 'player' || sm.winner === 'opponent')) {
      setServerWinner(sm.winner === 'player' ? 1 : 2);
    } else {
      setServerWinner(null);
    }
  }
}
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [battleId, battleIdProp]);

  async function settleBattle(
  battleId,
  playSig
) {
  let path;

  if (
    activeTournamentId &&
    activeTournamentMatchId
  ) {
    path =
      `/api/battle/tournaments/${encodeURIComponent(
        activeTournamentId
      )}/matches/${encodeURIComponent(
        activeTournamentMatchId
      )}/settle`;

  } else if (
    activeChallengeId
  ) {
    path =
      `/api/battle/challenges/${encodeURIComponent(
        activeChallengeId
      )}/settle`;

  } else {
    path =
      "/api/battle/bf8c3a/settlea";
  }

  const res =
    await fetch(
      `${process.env.REACT_APP_API_URL}${path}`,
      {
        method:
          "POST",

        headers: {
          "Content-Type":
            "application/json",

          Authorization:
            `Bearer ${localStorage.getItem(
              "token"
            )}`,

          "X-TG-INITDATA":
            window.Telegram
              ?.WebApp
              ?.initData || "",
        },

        body:
          JSON.stringify({
            battleId,
            playSig,
          }),
      }
    );

  const data =
    await res
      .json()
      .catch(
        () => ({})
      );

  if (!res.ok) {
    throw new Error(
      data?.error ||
      `Settlement failed (${res.status})`
    );
  }

  return data;
}

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const cancelMatchmaking = () => {
  matchmakingCancelledRef.current = true;

  if (matchmakingAbortRef.current) {
    matchmakingAbortRef.current.abort();
    matchmakingAbortRef.current = null;
  }

  setIsSkippingMatchmaking(false);
  setIntroActive(false);
  setOppStatus('searching');

  onNavigate(
    isArenaBattle ? 'arenas' : 'fight',
    isArenaBattle ? effectiveReturnParams : {}
  );
};

    async function skipMatchmakingAndLoadFallback(id) {
  if (!id || isSkippingMatchmaking) return null;

  setIsSkippingMatchmaking(true);

  try {
    const skipRes = await fetch(
      `${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/skip-matchmaking`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({ battleId: id }),
      }
    );

    const skipData = await skipRes.json().catch(() => ({}));

    if (!skipRes.ok) {
      throw new Error(skipData?.error || `Skip matchmaking failed (${skipRes.status})`);
    }

    const statusRes = await fetch(
      `${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/statusa/${encodeURIComponent(id)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
      }
    );

    const statusData = await statusRes.json().catch(() => ({}));

    if (!statusRes.ok) {
      throw new Error(statusData?.error || `Battle status failed (${statusRes.status})`);
    }

    return {
      battleId: statusData?.battleId || id,
      ...statusData,
    };
  } finally {
    setIsSkippingMatchmaking(false);
  }
}

      useEffect(() => {
  if (!debugBattleMode) return;

  const debugPlayer = {
    id: 'debug-player',
    name: 'Tapian',
    rarity: 'Common',

    attack: 13,
    defense: 13,
    maxHp: 27,
    hp: 27,

    visualAttack: 13,
    visualDefense: 13,
    visualHealth: 27,

    displayStats: {
      attack: 13,
      defense: 13,
      health: 27,
    },

    baseStats: {
      attack: 13,
      defense: 13,
      health: 27,
    },

    abilities: ['regen'],
    abilitiesSet: new Set(['regen']),

    side: 'good',

    character: null,
    characterLayers: [],
    art: '/pack.png',

    serverMeta: {
      source: 'debug',
    },
  };

  const debugOpponent = {
    id: 'debug-opponent',
    name: 'Prize Card',
    rarity: 'Common',

    attack: 11,
    defense: 8,
    maxHp: 29,
    hp: 29,

    visualAttack: 11,
    visualDefense: 8,
    visualHealth: 29,

    displayStats: {
      attack: 11,
      defense: 8,
      health: 29,
    },

    baseStats: {
      attack: 11,
      defense: 8,
      health: 29,
    },

    abilities: ['bleed'],
    abilitiesSet: new Set(['bleed']),

    side: 'bad',

    character: null,
    characterLayers: [],
    art: '/pack.png',

    serverMeta: {
      source: 'debug',
    },
  };

  /*
   * Script reproduzindo a batalha do battleEngine.
   * O importante é usar hpAfter e evt.hp exatamente
   * como o backend envia.
   */
  const debugScript = [
    {
      turn: 1,
      actor: 'player',
      type: 'attack',
      who: 'debug-player',
      target: 'debug-opponent',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 27,
        defender: 24,
      },
    },

    {
      turn: 2,
      actor: 'opponent',
      type: 'attack',
      who: 'debug-opponent',
      target: 'debug-player',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 24,
        defender: 22,
      },
    },

    {
      turn: 2,
      actor: 'opponent',
      type: 'ability',
      ability: 'bleed',
      who: 'debug-opponent',
    },

    {
      turn: 2,
      actor: 'opponent',
      type: 'dot',
      dot: 'bleed',
      who: 'debug-player',
      damage: 2,
      hp: 20,
    },

    {
      turn: 3,
      actor: 'player',
      type: 'regen',
      who: 'debug-player',
      heal: 2,
      hp: 22,
    },

    {
      turn: 3,
      actor: 'player',
      type: 'ability',
      ability: 'regen',
      who: 'debug-player',
      heal: 2,
    },

    {
      turn: 3,
      actor: 'player',
      type: 'dot',
      dot: 'bleed',
      who: 'debug-player',
      damage: 2,
      hp: 20,
    },

    {
      turn: 3,
      actor: 'player',
      type: 'attack',
      who: 'debug-player',
      target: 'debug-opponent',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 20,
        defender: 19,
      },
    },

    {
      turn: 4,
      actor: 'opponent',
      type: 'attack',
      who: 'debug-opponent',
      target: 'debug-player',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 19,
        defender: 15,
      },
    },

    {
      turn: 4,
      actor: 'opponent',
      type: 'dot',
      dot: 'bleed',
      who: 'debug-player',
      damage: 2,
      hp: 13,
    },

    {
      turn: 5,
      actor: 'player',
      type: 'attack',
      who: 'debug-player',
      target: 'debug-opponent',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 13,
        defender: 14,
      },
    },

    {
      turn: 6,
      actor: 'opponent',
      type: 'attack',
      who: 'debug-opponent',
      target: 'debug-player',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 14,
        defender: 8,
      },
    },

    {
      turn: 7,
      actor: 'player',
      type: 'attack',
      who: 'debug-player',
      target: 'debug-opponent',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 8,
        defender: 9,
      },
    },

    {
      turn: 8,
      actor: 'opponent',
      type: 'attack',
      who: 'debug-opponent',
      target: 'debug-player',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 9,
        defender: 3,
      },
    },

    {
      turn: 9,
      actor: 'player',
      type: 'attack',
      who: 'debug-player',
      target: 'debug-opponent',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 3,
        defender: 4,
      },
    },

    {
      turn: 10,
      actor: 'opponent',
      type: 'attack',
      who: 'debug-opponent',
      target: 'debug-player',
      damage: 5,
      blocked: false,
      hpAfter: {
        attacker: 4,
        defender: 0,
      },
    },

    {
      turn: 10,
      actor: 'opponent',
      type: 'end',
      winner: 'opponent',
      finalHp: {
        player: 0,
        opponent: 4,
      },
    },
  ];

  setP1(debugPlayer);
  setP2(debugOpponent);

  setIntroPlayer(debugPlayer);

  setBattleScript(debugScript);
  setScriptIndex(0);

  setServerWinner(2);
  setWinner(null);

  setBattleId('debug-reported-1');
  setPlaySig(null);

  setOppStatus('found');
  setIntroActive(false);

  setTurn(1);

  setLog([]);
  setLimitInfo(null);

  busyRef.current = false;

  statusRef.current = {
    'debug-player': {
      regenCharges: 1,
      aegisCharges: 0,
    },
    'debug-opponent': {
      regenCharges: 0,
      aegisCharges: 0,
    },
  };

  appliedOnceRef.current = {
    'debug-player': {
      flameburstApplied: false,
      bleedApplied: false,
    },
    'debug-opponent': {
      flameburstApplied: false,
      bleedApplied: false,
    },
  };

}, [debugBattleMode]);

async function waitForBattleReady(startData, opts = {}) {
  matchmakingCancelledRef.current = false;

  const throwIfCancelled = () => {
    if (matchmakingCancelledRef.current) {
      throw new Error("MATCHMAKING_CANCELLED");
    }
  };

  const { cardId, arenaId } = opts;

  if (startData?.opponent && startData?.playSig) {
    return startData;
  }

  const id = startData?.battleId;
  if (!id) {
    throw new Error("MATCHMAKING_NO_BATTLE_ID");
  }

  setBattleId(id);

  const pollEveryMs = Number(startData?.search?.pollEveryMs || 2000);
  const timeoutMs = Number(startData?.search?.timeoutMs || 25_000) + 10_000;

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    throwIfCancelled();

    const controller = new AbortController();
    matchmakingAbortRef.current = controller;

    let res;
    let data;

    try {
      res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/statusa/${encodeURIComponent(id)}`,
        {
          method: "GET",
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      data = await res.json().catch(() => ({}));
    } catch (err) {
      if (err?.name === "AbortError" || matchmakingCancelledRef.current) {
        throw new Error("MATCHMAKING_CANCELLED");
      }

      throw err;
    } finally {
      if (matchmakingAbortRef.current === controller) {
        matchmakingAbortRef.current = null;
      }
    }

    throwIfCancelled();

    if (!res.ok) {
      throw new Error(data?.error || `Battle status failed (${res.status})`);
    }

    if (data?.status === "READY" || data?.opponent) {
      return {
        ...startData,
        ...data,
        battleId: data?.battleId || startData?.battleId,
      };
    }

    if (data?.status === "SEARCHING" || data?.status === "WAITING" || !data?.status) {
      await sleep(pollEveryMs);
      throwIfCancelled();
      continue;
    }

    if (data?.status === "LIVE_EXPIRED_RETRY_START") {
      throwIfCancelled();

      if (!cardId) {
        throw new Error("LIVE_EXPIRED_RETRY_START_WITHOUT_CARD_ID");
      }

      const controller = new AbortController();
      matchmakingAbortRef.current = controller;

      let retryRes;
      let retryData;

      try {
        retryRes = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/starta`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'X-TG-INITDATA': window.Telegram?.WebApp?.initData || '',
          },
          body: JSON.stringify({ cardId, arenaId }),
        });

        retryData = await retryRes.json().catch(() => ({}));
      } catch (err) {
        if (err?.name === "AbortError" || matchmakingCancelledRef.current) {
          throw new Error("MATCHMAKING_CANCELLED");
        }

        throw err;
      } finally {
        if (matchmakingAbortRef.current === controller) {
          matchmakingAbortRef.current = null;
        }
      }

      throwIfCancelled();

      if (!retryRes.ok || !retryData?.battleId) {
        throw new Error(retryData?.error || `Retry start failed (${retryRes.status})`);
      }

      return waitForBattleReady(retryData, { cardId, arenaId });
    }

    if (data?.status === "EXPIRED") {
      throw new Error("LIVE_PVP_EXPIRED_BUT_BACKEND_DID_NOT_RETURN_FALLBACK");
    }

    if (data?.status === "FAILED" || data?.status === "CANCELLED") {
      throw new Error(data?.error || data?.status || "MATCHMAKING_FAILED");
    }

    await sleep(pollEveryMs);
    throwIfCancelled();
  }

  throw new Error("MATCHMAKING_TIMEOUT");
}

  const [motionOn, setMotionOn] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  const shieldActive = shieldSecondsLeft > 0;

  async function fetchBattleStreak() {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/streak`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `Streak failed (${res.status})`);
    return data;
  }

  const animDur = (s) => {
    if (!motionOn) return 0;
    if (skipToEnd) return 0;
    return s / Math.max(1, speed);
  };

  const waitImpact = () =>
    new Promise((res) => setTimeout(res, animDur(IMPACT_LINGER_S * TURN_SPEED) * 1000));

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setMotionOn(!mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    setBattleStreak(null);
  }, [battleId]);

  useEffect(() => {
    if (winner) firstBtnRef.current?.focus();
  }, [winner]);

  // If the backend winner arrives later than the local one, override.
  useEffect(() => {
    if (!serverWinner || !winner) return;
    if (serverWinner !== winner) {
      setWinner(serverWinner);
    }
  }, [serverWinner, winner]);

 useEffect(() => {
  if (debugBattleMode) return;

  if (
    !winner ||
    !p1?.id ||
    !battleId ||
    !playSig ||
    settlementPostedRef.current
  ) {
    return;
  }
    settlementPostedRef.current = true;

    (async () => {
      try {
        const forced = serverWinner;
        const didWin = typeof forced === 'number' ? forced === 1 : winner === 1;

        const data = await settleBattle(battleId, playSig);

        if (data?.user) {
          setUser?.((u) => ({ ...(u || {}), ...(data.user || {}) }));
        }

        const rewards = Array.isArray(data?.awardedRewards) ? data.awardedRewards : [];
        setAwardedRewards(rewards);
        setStreakReward(data?.streakReward ?? null);

        const chestLike =
          data?.chestWon ??
          data?.chest ??
          data?.newChest ??
          data?.rewardChest ??
          rewards.find((x) => x?.type === 'chest' || x?.kind === 'chest' || x?.rewardType === 'chest') ??
          null;


        if (didWin && chestLike) {
          const meta = getChestMetaFromChest(chestLike);
          enqueueModal('chest', meta);
        }

        const arenaPrizeDrops = Array.isArray(data?.awardedArenaTrophyPrizes)
          ? data.awardedArenaTrophyPrizes
          : [];

        if (arenaPrizeDrops.length > 0) {
          enqueueModal('prizes', arenaPrizeDrops);
        }


        try {
          const s = await fetchBattleStreak();
          setBattleStreak(s);
        } catch {}

        if (data?.streakReward?.amount) {
          const sr = data.streakReward;
          const label = sr.type === 'sparks' ? 'Sparks' : 'Runes';
          pushLog(`🔥 Milestone ${sr.milestone}! +${sr.amount} ${label}`);
        }

        // Optional: log it
        rewards.forEach((r) => {
          if (r?.isFinalFragment) {
            pushLog(
              `🎉 Last fragment collected for ${r.itemName}! You can now claim it in Inventory.`,
            );
          }
        });

        const arenaCompletion =
  data?.arenaCompletion ??
  data?.arena_completion ??
  data?.arena_completed ?? // caso você tenha usado outro nome no backend
  null;

if (arenaCompletion?.completed) {
  const completedArenaId =
    arenaCompletion?.arenaId ??
    arenaCompletion?.arena_id ??
    null;

  const nextArenaId =
    arenaCompletion?.nextArenaId ??
    arenaCompletion?.next_arena_id ??
    arenaCompletion?.toArenaId ??
    arenaCompletion?.to_arena_id ??
    null;

  const allCompleted =
    arenaCompletion?.allCompleted ??
    arenaCompletion?.all_completed ??
    arenaCompletion?.allArenasCompleted ??
    arenaCompletion?.all_arenas_completed ??
    false;

  let shouldShowModal = true;

  // Só tratamos diferente quando realmente acabou TODAS as arenas.
  if (allCompleted || !nextArenaId) {
    const seenKey = completedArenaId
      ? `all_arenas_completed_seen:${completedArenaId}`
      : null;

    if (seenKey) {
      const alreadySeen = localStorage.getItem(seenKey) === "1";

      if (alreadySeen) {
        shouldShowModal = false;
      } else {
        localStorage.setItem(seenKey, "1");
      }
    }
  }

  if (shouldShowModal) {
    enqueueModal('arena', {
      arenaId: completedArenaId,

      nextArenaId,

      allCompleted,

      trophies: arenaCompletion?.trophies ?? null,

      trophyMin:
        arenaCompletion?.trophyMin ??
        arenaCompletion?.trophy_min ??
        null,

      trophyMax:
        arenaCompletion?.trophyMax ??
        arenaCompletion?.trophy_max ??
        null,
    });
  }
}

if (data?.user) {
  setUser?.((prev) => ({
    ...(prev || {}),
    ...data.user,
  }));
}

            
        const entryFee = Number(data?.entryFee ?? 0);
const payout = Number(data?.payout ?? 0);
const shield = data?.shield || null;
const shouldShowShield = isArenaBattle && !!shield?.blockedLoss;

if (didWin) {
  setShieldLossProtected(false);
  setShieldRefundedAmount(0);

  pushLog(`Victory reward: +${payout}`);

  if (isSpecialBattle) {
    setShowAdRewardButton(false);
  } else {
    setShowAdRewardButton(
  getBattleAdUsage("win") < DAILY_WIN_AD_LIMIT
);
  }
} else if (!isSpecialBattle && entryFee > 0) {
  if (shouldShowShield) {
    setShieldLossProtected(true);
    setShieldRefundedAmount(Number(shield?.refunded ?? entryFee ?? 0));

    setShowLossRefundButton(false);
    setLossRefundPercent(0);
  } else {
    setShieldLossProtected(false);
    setShieldRefundedAmount(0);

    const canUseLossAd =
  getBattleAdUsage("loss") <
  DAILY_LOSS_AD_LIMIT;

if (canUseLossAd) {
  const refundPercent =
    Math.random() * 0.7 + 0.3;

  setLossRefundPercent(
    Math.floor(refundPercent * 100)
  );

  setShowLossRefundButton(true);
} else {
  setLossRefundPercent(0);
  setShowLossRefundButton(false);
}
  }
} else if (isSpecialBattle) {
  setShowLossRefundButton(false);
  setLossRefundPercent(0);
}
      } catch (e) {
        console.warn('[battle] settlement error:', e.message);
        settlementPostedRef.current = false;
      }
    })();
  }, [
  winner,
  p1?.id,
  battleId,
  playSig,
  serverWinner,
  isArenaBattle,
  isChallengeBattle,
  isTournamentBattle,
  isSpecialBattle,
  debugBattleMode,
]);

  /* ---------------- floaters / log / notices ---------------- */
  const noticeTimersRef = useRef([]);
  const floaterTimersRef = useRef([]);
  const pushNoticeTimer = (id) => {
    noticeTimersRef.current.push(id);
  };
  const pushFloaterTimer = (id) => {
    floaterTimersRef.current.push(id);
  };

  const clearNotices = () => {
    noticeTimersRef.current.forEach(clearTimeout);
    noticeTimersRef.current = [];
    noticeQueueRef.current = [];
    showingRef.current = false;
    setNotice(null);
  };

  const clearFloaters = () => {
    floaterTimersRef.current.forEach(clearTimeout);
    floaterTimersRef.current = [];
    setFloaters([]);
  };

  function showNextNotice() {
    if (showingRef.current) return;
    const next = noticeQueueRef.current.shift();
    if (!next) return;

    showingRef.current = true;
    setNotice(next);

    const visibleMs = animDur((next.duration ?? 1.2) * TURN_SPEED) * 1000;
    pushNoticeTimer(
      setTimeout(() => {
        setNotice(null);
        pushNoticeTimer(
          setTimeout(() => {
            showingRef.current = false;
            showNextNotice();
          }, NOTICE_EXIT_MS),
        );
      }, visibleMs),
    );
  }

  function enqueueNotice(n) {
    noticeQueueRef.current.push(n);
    if (!showingRef.current) showNextNotice();
  }

  const pushLog = (t) => {
    setLog((L) => [t, ...L].slice(0, 8));
    const id = Math.random().toString(36).slice(2);
    const color =
      t.includes('burn') || t.includes('bleed')
        ? '#ff6666'
        : t.includes('regen')
        ? '#4eff9b'
        : t.includes('Aegis') || t.includes('purifies')
        ? '#5dcaff'
        : t.includes('Defeat')
        ? '#ff4444'
        : t.includes('Victory') || t.includes('You win!')
        ? '#ffe15f'
        : '#ffffff';
    enqueueNotice({ id, text: t, color, duration: 1.2 });
  };

  const spawnFloater = (text, x, y, color = '#fff', duration = 0.9) => {
    const id = Math.random().toString(36).slice(2);
    setFloaters((f) => [...f, { id, text, x, y, color, duration }]);
    const t = setTimeout(
      () => setFloaters((f) => f.filter((fl) => fl.id !== id)),
      animDur(duration * TURN_SPEED) * 1000,
    );
    pushFloaterTimer(t);
  };

  useEffect(() => {
    return () => {
      clearNotices();
      clearFloaters();
    };
  }, []);

  /* ---------------- Load player/enemy safely ---------------- */
  const isValidId = (v) =>
    (typeof v === 'string' || typeof v === 'number') && String(v).trim().length > 0;

  // inside export default function Battle(...) — after your useState/useRef, etc.
  const audioCtxRef = useRef(null);
  const firstBtnRef = useRef(null);

  const projectilePath = React.useMemo(() => {
    if (!projectile) return null;
    return makeZigZag(projectile.from, projectile.to, { segments: 7, amp: 22 });
    // You can keep deps coarse (projectile?.id) or explicit like below:
  }, [
    projectile?.id,
    projectile?.from?.x,
    projectile?.from?.y,
    projectile?.to?.x,
    projectile?.to?.y,
  ]);

  const ensureConfettiStyles = () => {
    if (document.getElementById('confetti-styles')) return;
    const css = `
    @keyframes confetti-fall {
      0%   { transform: translateY(-100vh) rotate(0deg);   opacity: 0; }
      8%   { opacity: 1; }
      100% { transform: translateY(100vh)  rotate(720deg); opacity: 0; }
    }
    .confetti-overlay {
      position: fixed; inset: 0; pointer-events: none; z-index: 10000; overflow: hidden;
      contain: layout paint;
    }
    .confetti-piece {
      position: absolute; top: 0; left: 0;
      font-size: 16px; line-height: 1; text-shadow: 0 1px 0 #000;
      will-change: transform, opacity;
      opacity: 0;
      transform: translateY(-120vh) rotate(0deg);
      animation-name: confetti-fall;
      animation-timing-function: ease-in;
      animation-fill-mode: both;
    }
  `;
    const style = document.createElement('style');
    style.id = 'confetti-styles';
    style.textContent = css;
    document.head.appendChild(style);
  };

  const spawnConfettiBurst = (count = 80) => {
    ensureConfettiStyles();
    const overlay = document.createElement('div');
    overlay.className = 'confetti-overlay';
    const symbols = ['✦', '★', '✧', '◆', '●', '■', '❤', '♦', '♢', '✪'];
    for (let i = 0; i < count; i++) {
      const el = document.createElement('span');
      el.className = 'confetti-piece';
      el.textContent = symbols[Math.floor(Math.random() * symbols.length)];
      const delay = Math.random() * 0.25;
      const duration = 1.6 + Math.random() * 2.2;
      const hue = Math.floor(Math.random() * 360);
      const size = 14 + Math.random() * 10;
      el.style.left = `${Math.random() * 100}vw`;
      el.style.animationDuration = `${duration}s`;
      el.style.animationDelay = `${delay}s`;
      el.style.color = `hsl(${hue} 95% 60%)`;
      el.style.fontSize = `${size}px`;
      overlay.appendChild(el);
    }
    document.body.appendChild(overlay);
    setTimeout(() => {
      try {
        document.body.removeChild(overlay);
      } catch {}
    }, 4000);
  };

  const ensureAudio = async () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    const ctx = audioCtxRef.current || new Ctx();
    audioCtxRef.current = ctx;
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {}
    }
    return ctx;
  };

  const playChime = async () => {
    try {
      const ctx = await ensureAudio();
      if (!ctx) return;

      const vol = Math.max(0, Math.min(1, Number(sfxVolume ?? 1)));

      const now = ctx.currentTime;
      const notes = [
        { t: 0.0, f: 523.25, d: 0.16 },
        { t: 0.1, f: 659.25, d: 0.16 },
        { t: 0.2, f: 783.99, d: 0.18 },
        { t: 0.36, f: 1046.5, d: 0.22 },
      ];

      notes.forEach(({ t, f, d }, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = i === notes.length - 1 ? 'triangle' : 'sine';
        o.frequency.value = f;
        g.gain.value = 0;
        o.connect(g);
        g.connect(ctx.destination);

        const start = now + t;
        const end = start + d;

        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(0.28 * vol, start + 0.02); // ✅ scaled
        g.gain.exponentialRampToValueAtTime(0.0001, end);

        o.start(start);
        o.stop(end + 0.02);
      });
    } catch {}
  };

  const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));

  const playDefeatChime = async () => {
    if (!sfxEnabled) return;

    const ctx = await ensureAudio();
    if (!ctx) return;

    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.value = 0.22 * clamp01(sfxVolume);
    master.connect(ctx.destination);

    const notes = [392, 349, 330, 262];
    const dur = 0.11;

    notes.forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();

      o.type = 'triangle';
      o.frequency.setValueAtTime(freq, now + i * dur);

      const t0 = now + i * dur;
      const t1 = t0 + dur;

      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.9, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t1);

      o.connect(g);
      g.connect(master);

      o.start(t0);
      o.stop(t1 + 0.02);
    });
  };

  const celebrate = (intensity = 'normal') => {
    spawnConfettiBurst(intensity === 'big' ? 120 : 80);
    if (sfxEnabled) playChime();
  };

  // Initialize Adsgram SDK
  useEffect(() => {
    if (typeof window !== 'undefined' && window.Adsgram && !adControllerRef.current) {
      try {
        adControllerRef.current = window.Adsgram.init({
          blockId: '22642',
        });
        console.log('[Adsgram] SDK initialized');
      } catch (e) {
        console.error('[Adsgram] Failed to initialize SDK:', e);
      }
    }
  }, []);

  // Separate Adsgram block for loss-refund flow (user lost but can get entry fee back)
  useEffect(() => {
    const blockId = process.env.REACT_APP_ADSGRAM_BATTLE_LOSS_REFUND_BLOCK_ID || '22715';

    if (
      typeof window !== 'undefined' &&
      window.Adsgram &&
      blockId &&
      !lossRefundAdControllerRef.current
    ) {
      try {
        lossRefundAdControllerRef.current = window.Adsgram.init({
          blockId,
        });
        console.log('[Adsgram] Loss-refund block initialized');
      } catch (e) {
        console.warn('[Adsgram] Loss-refund init failed:', e);
      }
    }
  }, []);

  // Handle Adsgram ad reward for 2x battle rewards
  const handleAdReward = useCallback(async () => {
  if (isSpecialBattle) {
    return;
  }

  try {
      setAdRewardClaimed(true);

      // Call backend to double the rewards
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/ad-reward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ battleId, playSig }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.warn('[ad-reward] failed:', data?.error);
        pushLog('Failed to claim 2x reward');
        setAdRewardClaimed(false);
        return;
      }

          incrementBattleAdUsage("win");

      // Update user balance
      if (data?.user) {
        setUser?.((u) => ({ ...(u || {}), ...(data.user || {}) }));
      }

      pushLog('🎉 2x Reward claimed!');
      celebrate('big');
    } catch (e) {
      console.warn('[ad-reward] error:', e.message);
      pushLog('Failed to claim 2x reward');
      setAdRewardClaimed(false);
    }
  }, [
  battleId,
  playSig,
  setUser,
  isSpecialBattle,
]);

          const getScriptUnitSide = useCallback(
  (evt) => {
    if (!evt) return null;

    const who =
      evt.who !== undefined &&
      evt.who !== null
        ? String(evt.who)
        : null;

    /*
     * Snapshots generated by the backend.
     * They contain the IDs used by battleEngine whenever
     * those IDs are available.
     */
    const playerScriptId =
      p2?.serverMeta
        ?.playerSnapshot
        ?.id;

    const opponentScriptId =
      p2?.serverMeta
        ?.opponentSnapshot
        ?.id;

    if (
      who &&
      playerScriptId != null &&
      who === String(playerScriptId)
    ) {
      return 'player';
    }

    if (
      who &&
      opponentScriptId != null &&
      who === String(opponentScriptId)
    ) {
      return 'opponent';
    }

    /*
     * AI / Defense Deck currently use the engine fallback
     * for the player because playerCard has no id.
     */
    if (who === 'unit') {
      return 'player';
    }

    /*
     * Try frontend IDs as another safe fallback.
     */
    if (
      who &&
      p1?.id != null &&
      who === String(p1.id)
    ) {
      return 'player';
    }

    if (
      who &&
      p2?.id != null &&
      who === String(p2.id)
    ) {
      return 'opponent';
    }

    return null;
  },
  [
    p1?.id,
    p2?.id,
    p2?.serverMeta,
  ]
);

  const handleWatchAdClick = async () => {
  if (isSpecialBattle) {
    return;
  }

  if (!adControllerRef.current) {
      pushLog('Ad service not available');
      return;
    }

    setIsWatchingAd(true);

    try {
      // Show full-screen ad
      await adControllerRef.current.show();

      // Ad was watched till the end
      console.log('[Adsgram] Ad watched successfully');
      await handleAdReward();
    } catch (error) {
      // Ad was skipped, closed, or encountered an error
      console.warn('[Adsgram] Ad error or skipped:', error);

      if (error?.error) {
        pushLog('Ad failed to load - please try again');
      } else {
        pushLog('Ad was skipped or closed');
      }
    } finally {
      setIsWatchingAd(false);
    }
  };

  // Loss refund: user watched an ad after losing, now call backend to refund entry fee
  const handleLossRefundReward = useCallback(
  async (percent) => {
    if (isSpecialBattle) {
      return;
    }

    try {
        setLossRefundClaimed(true);

        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/ad-loss-refund`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            battleId,
            playSig,
            percent: percent / 100,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          console.warn('[ad-loss-refund] failed:', data?.error);
          pushLog('Failed to refund Sparks from ad');
          setLossRefundClaimed(false);
          return;
        }

        incrementBattleAdUsage("loss");

        if (data?.user) {
          setUser?.((u) => ({ ...(u || {}), ...(data.user || {}) }));
        }

        const refunded = Number(data?.refunded ?? 0);
        if (refunded > 0) {
          pushLog(`💸 Sparks refunded from ad: +${refunded}`);
        } else {
          pushLog('Sparks refunded from ad');
        }
        celebrate('normal');
      } catch (e) {
        console.warn('[ad-loss-refund] error:', e.message);
        pushLog('Failed to refund Sparks from ad');
        setLossRefundClaimed(false);
      }
    },
    [
  battleId,
  playSig,
  setUser,
  isSpecialBattle,
],
  );

  const handleWatchLossRefundAdClick = async (percent) => {
  if (isSpecialBattle) {
    return;
  }

  if (!lossRefundAdControllerRef.current) {
      pushLog('Ad service not available');
      return;
    }

    setIsWatchingAd(true);

    try {
      await lossRefundAdControllerRef.current.show();
      console.log('[Adsgram] Loss refund ad watched successfully');
      await handleLossRefundReward(percent);
      setShowLossRefundButton(false);
    } catch (error) {
      console.warn('[Adsgram] Loss refund ad error or skipped:', error);

      if (error?.error) {
        pushLog('Ad failed to load - please try again');
      } else {
        pushLog('Ad was skipped or closed');
      }
    } finally {
      setIsWatchingAd(false);
    }
  };

  const handleSkipToEndClick = async () => {
    try {
      if (!isSpecialBattle && ADS_ENABLED && Math.random() < 0.3) {
        if (typeof window !== 'undefined' && window.Adsgram) {
          const skipAdController = window.Adsgram.init({
            blockId: 'int-22701',
          });

          await skipAdController.show();
        }
      }
    } catch (e) {
      console.warn('[Adsgram] Skip battle ad error:', e);
    } finally {
      setSkipToEnd(true);
    }
  };

  // tidy up audio when Battle unmounts
  useEffect(() => {
    return () => {
      try {
        audioCtxRef.current?.close();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!winner) return;

    if (winner === 1) {
      celebrate('big');
    } else if (winner === 2) {
      playDefeatChime();
    }

    setIsPaused(false);
  }, [winner]);

  useEffect(() => {

           if (debugBattleMode) return;

    let mounted = true;

    const loadById = async (id) => {


      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/cards/${encodeURIComponent(id)}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        },
      );
      if (!mounted) return null;
      const data = await res.json();
      return data.card || null;
    };

    const loadFirstOwned = async () => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/shop/mine`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!mounted) return null;
      const data = await res.json();
      return (data.cards && data.cards[0]) || null;
    };

    (async () => {
      try {
        let card = null;
        const storedId = localStorage.getItem('selectedCardId');
        const effectiveId = isValidId(cardIdProp)
          ? cardIdProp
          : isValidId(selectedCardId)
          ? selectedCardId
          : isValidId(storedId)
          ? storedId
          : null;

        if (isValidId(effectiveId)) {
          card = await loadById(effectiveId);
        } else {
          card = await loadFirstOwned();
        }
        if (!mounted || !card) return;

        if (card?.id) localStorage.setItem('selectedCardId', String(card.id));

        const abilities = Array.isArray(card.abilities)
          ? card.abilities
          : (() => {
              try {
                return JSON.parse(card.abilities || '[]');
              } catch {
                return [];
              }
            })();
        const list = abilities.map((a) => (typeof a === 'string' ? a : a?.name)).filter(Boolean);
        const abilitiesSet = new Set(list.map(toId));

        let me = {
  id: card.id,
  name: card.name || 'Your Hero',
  rarity: card.rarity || 'Common',

  level: Number(
    card.level ??
    card.cardLevel ??
    card.character?.level ??
    1
  ),

  wins: Number(card.wins ?? 0),
  attack: Number(card.attack || 1),
  defense: Number(card.defense || 0),
  maxHp: Number(card.health || 10),
  hp: Number(card.health || 10),
          abilities: list,
          abilitiesSet,
          side: user?.side || 'good',
          character: card.character,
          characterLayers: characterLayersFrom(card), // ⬅️ pass the full card
          art: card?.character?.body
            ? `/assets/body/${card.character.body.side}/${card.character.body.rarity}/${card.character.body.variant}_thumb.png`
            : user?.side === 'good'
            ? '/sun.png'
            : '/moon.png',
        };

        // passive stat edits from abilities
        if (!me.base) {
          me.base = {
            attack: me.attack,
            defense: me.defense,
            maxHp: me.maxHp,
            hp: me.hp,
          };
        }

        // passive stat edits from abilities (battle values)
        if (has(me, 'fortify')) {
          me.maxHp += 5;
          me.hp += 5;
        }
        if (has(me, 'shield wall')) me.defense += 3;

        setIntroPlayer(me);

        // build a fair enemy (no bias)
        //let foe = rollEnemyAround(me, 0.0);

        // === Prefer server-provided opponent ===
        let foe = null;

        // A) If /active already gave us an opponent, use it
        if (pendingOpponent) {
          foe = normalizeUnitFromApi(pendingOpponent);
        }

        // B) Otherwise start a new battle to get the opponent from the backend
        if (
  !foe &&
  !isChallengeBattle &&
  !isTournamentBattle
) {
          try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/starta`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'X-TG-INITDATA': window.Telegram?.WebApp?.initData || '',
              },
              body: JSON.stringify({ cardId: me.id, arenaId: effectiveArenaId }),
            });
            let data = await res.json().catch(() => ({}));

            if (!res.ok || !data?.battleId) {
              const code = String(data?.error || '');
              if (code === 'HUMAN_CHECK_REQUIRED') {
                onNavigate('humancheck', {
                  returnTo: 'fight',
                  returnParams: {
                    openedVia,
                  },
                });
                return;
              }

              const msg = String(data?.error || '').toLowerCase();

              if (res.status === 429 && (data?.error === 'COOLDOWN' || msg.includes('cooldown'))) {
                handleCooldown(data);

                setIntroActive(false);
                setTurn(0);
                return;
              }

              if (data?.error === 'DAILY_LIMIT' || msg.includes('daily_limit')) {
                setLimitInfo({
                  limit: Number(data?.limit ?? 5),
                  used: Number(data?.used ?? 5),
                  resetAt: data?.resetAt || null,
                });
                pushLog(`Daily limit reached (${data?.used ?? '?'} / ${data?.limit ?? '?'})`);
                const id = Math.random().toString(36).slice(2);
                enqueueNotice({
                  id,
                  text: 'Daily limit reached for this card',
                  color: '#ffb347',
                  duration: 2.0,
                });

                // stop the intro and leave user at the screen with the disabled button
                setIntroActive(false);
                setTurn(0);
                return;
              }

              if (
                res.status === 402 ||
                msg.includes('insufficient') ||
                msg.includes('spark') ||
                msg.includes('fund')
              ) {
                pushLog('No funds');
                // Leave intro so the user can click Back or New Fight (guarded)
                setIntroActive(false);
                setTurn(0);
                const id = Math.random().toString(36).slice(2);
                enqueueNotice({
                  id,
                  text: 'No funds',
                  color: '#ff6b6b',
                  duration: 1.6,
                });
                firstBtnRef.current?.showHint?.();
                return;
              }
              throw new Error(data?.error || `Failed to start (${res.status})`);
            }

           setBattleId(data.battleId);

if (data?.dailyLimit) {
  setLimitInfo({
    used: Number(data.dailyLimit.used || 0),
    limit: Number(data.dailyLimit.limit || 0),
  });
}

data = await waitForBattleReady(data, {
  cardId: me.id,
  arenaId: effectiveArenaId,
});

setBattleId(data.battleId);
setPlaySig(data.playSig || null);

if (data?.user) {
  setUser?.((prev) => ({
    ...(prev || {}),
    ...(data.user || {}),
  }));
}
              

            const sm = data?.opponent?.serverMeta;
            if (sm && (sm.winner === 'player' || sm.winner === 'opponent')) {
              setServerWinner(sm.winner === 'player' ? 1 : 2);
            } else {
              setServerWinner(null);
            }

            foe = normalizeUnitFromApi(data?.opponent);

            setBattleScript(sm?.script || data.script || null);
            setScriptIndex(0);
          } catch (e) {
  if (String(e?.message) === "MATCHMAKING_CANCELLED") {
    return;
  }

  console.warn('[start error]', e);
}
        }

              if (
  !foe &&
  isTournamentBattle
) {
  console.error(
    "[TOURNAMENT BATTLE] Invalid tournament opponent:",
    pendingOpponent
  );

  pushLog(
    "Tournament opponent could not be loaded."
  );

  setIntroActive(false);
  setTurn(0);

  return;
}

        // C) Fallback to local roll if server didn’t provide an opponent
        if (!foe) {
          pushLog('Matchmaking failed. Please try again.');
          setIntroActive(false);
          setTurn(0);
          return;
        }

        if (!foe?.id) {
          pushLog('Matchmaking failed. Please try again.');
          setIntroActive(false);
          setTurn(0);
          return;
        }

        // Ensure visuals if the API didn’t send character/layers/art
        if (!foe.characterLayers?.length && !foe.art) {
          pushLog('Opponent assets missing. Please try again.');
          setIntroActive(false);
          setTurn(0);
          return;
        }

       const foeSource = String(foe?.serverMeta?.source || '').toLowerCase();
const shouldApplyFrontendPassiveBuffs = foeSource !== 'defense_deck';

if (shouldApplyFrontendPassiveBuffs && has(foe, 'fortify')) {
  foe.maxHp += 5;
  foe.hp += 5;
}
if (shouldApplyFrontendPassiveBuffs && has(foe, 'shield wall')) {
  foe.defense += 3;
}

        // randomize who starts (NO hidden edge)
        initialTurnRef.current = Math.random() < 0.5 ? 1 : 2;

        // initialize status (regen/aegis are single-charge opens)
        statusRef.current = {
          [me.id]: {
            regenCharges: has(me, 'regen') ? 1 : 0,
            aegisCharges: has(me, 'aegis') ? 1 : 0,
          },
          [foe.id]: {
            regenCharges: has(foe, 'regen') ? 1 : 0,
            aegisCharges: has(foe, 'aegis') ? 1 : 0,
          },
        };
        appliedOnceRef.current = {
          [me.id]: { flameburstApplied: false, bleedApplied: false },
          [foe.id]: { flameburstApplied: false, bleedApplied: false },
        };

        statsRef.current = { turns: 0, p1Damage: 0, p2Damage: 0, abilities: 0 };

        clearNotices();
setP1(me);
setP2(foe);
setWinner(null);
setLog([]);
setTurn(0);

setOppStatus(
  isSpecialBattle
    ? "found"
    : "searching"
);

setArenaBg(randomBattleBg());
      } catch (e) {
        console.error('Failed to load card(s):', e);
      }
    })();

    return () => {
  mounted = false;
  matchmakingCancelledRef.current = true;

  if (matchmakingAbortRef.current) {
    matchmakingAbortRef.current.abort();
    matchmakingAbortRef.current = null;
  }
};
  }, [selectedCardId, user?.side, pendingOpponent,debugBattleMode]);

  /* ---------------- Geometry helpers ---------------- */
  const centerInArena = (el) => {
    const arena = arenaRef.current?.getBoundingClientRect();
    const r = el?.getBoundingClientRect();
    if (!arena || !r) return { x: 0, y: 0 };
    return {
      x: r.left - arena.left + r.width / 2,
      y: r.top - arena.top + r.height / 2,
    };
  };

  /* ---------------- Start-of-turn effects (REGEN ONLY) ---------------- */
  const startOfTurn = (unit) => {
    let hp = unit.hp;
    let s = statusRef.current[unit.id] || {};
    const notes = [];

    const isP1 = unit.id === p1?.id;
    const ref = isP1 ? p1Ref.current : p2Ref.current;
    const c = centerInArena(ref);

    if ((s.regenCharges ?? 0) > 0) {
      const underDot = (s.burn && s.burn > 0) || (s.bleed && s.bleed > 0);
      let heal = underDot ? 1 : 2;
      if (heal > 0) {
        hp = clamp(hp + heal, 0, unit.maxHp);
        notes.push(`regen +${heal}`);
        flashAbility(unit.id, 'regen');
        spawnFloater(`+${heal}`, c.x, c.y - 42, '#3cff8b');
      }
      s = { ...s, regenCharges: Math.max(0, (s.regenCharges || 0) - 1) };
    }

    if (notes.length) pushLog(`${unit.name} ${notes.join(', ')}`);
    statusRef.current[unit.id] = s;
    return { ...unit, hp };
  };

  /* ---------------- One attack (anim + damage + on-hit) ---------------- */
  const attackOnce = async (attacker, defender, attackerSide, scale = 1, opts = {}) => {
    const src = attackerSide === 'left' ? p1Ref.current : p2Ref.current;
    const dst = attackerSide === 'left' ? p2Ref.current : p1Ref.current;
    const from = centerInArena(src);
    const to = centerInArena(dst);
    const { canApplyStatus = true } = opts;

    const sDef = statusRef.current[defender.id] || {};
    const negated = (sDef.aegisCharges ?? 0) > 0;

    let dmg = computeDamage(attacker, defender);
    dmg = Math.max(1, Math.floor(dmg * scale));

    // 💥 NO more forced-winner clamp here.

    // Execution bonus
    if (has(attacker, 'execution') && defender.hp <= Math.ceil(defender.maxHp * 0.15)) {
      dmg += 3;
      pushLog(`${attacker.name} uses Execution!`);
      flashAbility(attacker.id, 'execution');
      statsRef.current.abilities += 1;
      spawnFloater('⚡', to.x, to.y, '#ffd966');
    }

    // Projectile anim
    const projType = attackerSide === 'left' ? 'bad' : 'good';
    const shotId = Math.random().toString(36).slice(2);
    setProjectile({ id: shotId, from, to, type: projType });

    await new Promise((r) => setTimeout(r, animDur(PROJ_TRAVEL_S * TURN_SPEED) * 1000));
    setProjectile(null);

    setImpact({
      x: to.x,
      y: to.y,
      type: projType,
      id: Math.random().toString(36).slice(2),
    });
    setTimeout(() => setImpact(null), animDur(IMPACT_LINGER_S * TURN_SPEED) * 1000);

    await waitImpact();

    let atk = { ...attacker };
    let def = { ...defender };

    if (!negated) {
      // core damage
      def.hp = clamp(def.hp - dmg, 0, def.maxHp);
      spawnFloater(`-${dmg}`, to.x, to.y - 60, '#ff4d4d');

      // stats
      if (attackerSide === 'left') statsRef.current.p1Damage += dmg;
      else statsRef.current.p2Damage += dmg;

      // Thorns reflect (no forced-winner clamp here either)
      if (has(defender, 'thorns') && dmg >= 2) {
        let reflect = Math.min(Math.max(1, Math.floor(dmg * 0.15)), Math.ceil(atk.maxHp * 0.15));

        if (reflect > 0) {
          atk.hp = clamp(atk.hp - reflect, 0, atk.maxHp);
          pushLog(`${defender.name}'s Thorns reflect ${reflect}`);
          flashAbility(defender.id, 'thorns');
          statsRef.current.abilities += 1;
          spawnFloater(`-${reflect}`, from.x, from.y - 60, '#ffb347');
        }
      }

      // On-hit status (only once per target per battle)
      if (canApplyStatus) {
        const once = appliedOnceRef.current[defender.id] || {
          flameburstApplied: false,
          bleedApplied: false,
        };

        if (has(attacker, 'flameburst') && !once.flameburstApplied && !(sDef.burn > 0)) {
          statusRef.current[defender.id] = { ...sDef, burn: BURN_START };
          appliedOnceRef.current[defender.id] = {
            ...once,
            flameburstApplied: true,
          };
          pushLog(`${defender.name} is burning!`);
          flashAbility(attacker.id, 'flameburst');
          statsRef.current.abilities += 1;
          spawnFloater('🔥', to.x, to.y, '#ff9933');
        }

        const afterBurn = statusRef.current[defender.id] || sDef;

        if (has(attacker, 'bleed') && !once.bleedApplied && !(afterBurn.bleed > 0)) {
          statusRef.current[defender.id] = { ...afterBurn, bleed: BLEED_START };
          appliedOnceRef.current[defender.id] = {
            ...(appliedOnceRef.current[defender.id] || once),
            bleedApplied: true,
          };
          pushLog(`${defender.name} is bleeding!`);
          flashAbility(attacker.id, 'bleed');
          statsRef.current.abilities += 1;
          spawnFloater('💉', to.x, to.y, '#ff3333');
        }
      }

      // Berserk
      if (has(atk, 'berserk') && !atk._berserkedOnce) {
        atk.attack = atk.attack + 2;
        atk._berserkedOnce = true;
        pushLog(`${attacker.name} goes Berserk (+2 ATK)!`);
        flashAbility(attacker.id, 'berserk');
        statsRef.current.abilities += 1;
        spawnFloater('💥', from.x, from.y, '#ffd966');
      }
    } else {
      const charges = Math.max(0, (sDef.aegisCharges ?? 0) - 1);
      statusRef.current[defender.id] = { ...sDef, aegisCharges: charges };
      pushLog(`${defender.name}'s Aegis negates the hit!`);
      flashAbility(defender.id, 'aegis');
      statsRef.current.abilities += 1;
      spawnFloater('🛡️', to.x, to.y, '#6dcfff');
    }

    return { A: atk, D: def };
  };

  useEffect(() => {
    if (!battleScript || !Array.isArray(battleScript)) return;
    if (!p1 || !p2) return;
    if (winner) return;
    if (scriptIndex >= battleScript.length) return;
    if (busyRef.current) return;

    if (introActive) return;

    if (!arenaRef.current || !p1Ref.current || !p2Ref.current) return;

    busyRef.current = true;
    let cancelled = false;

    const runStep = async () => {
      const evt = battleScript[scriptIndex];
      if (!evt) {
        busyRef.current = false;
        return;
      }

      await waitIfPaused();

      if (evt.type === 'attack') {
        const actorIsP1 = evt.actor === 'player';
        const projType = actorIsP1 ? 'bad' : 'good';

        // Turn banner + attacker glow
        setTurn(actorIsP1 ? 1 : 2);
        setIsActing(true);

        // Small windup while the avatar lunges
        await new Promise((r) => setTimeout(r, animDur(PRE_HIT_PAUSE_S * TURN_SPEED) * 1000));
        if (cancelled) return;

        // ✅ NOW read live positions
        const srcRef = actorIsP1 ? p1Ref.current : p2Ref.current;
        const dstRef = actorIsP1 ? p2Ref.current : p1Ref.current;

        if (!arenaRef.current || !srcRef || !dstRef) {
          busyRef.current = false;
          return;
        }

        const from = centerInArena(srcRef);
        const to = centerInArena(dstRef);

        const shotId = Math.random().toString(36).slice(2);
        setProjectile({ id: shotId, from, to, type: projType });

        await new Promise((r) => setTimeout(r, animDur(PROJ_TRAVEL_S * TURN_SPEED) * 1000));
        setProjectile(null);

        if (cancelled) return;

        setImpact({
          x: to.x,
          y: to.y,
          type: projType,
          id: Math.random().toString(36).slice(2),
        });
        setTimeout(() => setImpact(null), animDur(IMPACT_LINGER_S * TURN_SPEED) * 1000);

        await waitImpact();

        const dmg = Number(evt.damage || 0);

const attackerHp = Number(
  evt?.hpAfter?.attacker
);

const defenderHp = Number(
  evt?.hpAfter?.defender
);

if (actorIsP1) {
  /*
   * PLAYER attacked OPPONENT.
   *
   * IMPORTANT:
   * Do NOT calculate HP here.
   * The backend already calculated the exact battle state.
   */

  if (Number.isFinite(attackerHp)) {
    setP1((prev) =>
      prev
        ? {
            ...prev,
            hp: clamp(
              attackerHp,
              0,
              prev.maxHp
            ),
          }
        : prev
    );
  }

  if (Number.isFinite(defenderHp)) {
    setP2((prev) =>
      prev
        ? {
            ...prev,
            hp: clamp(
              defenderHp,
              0,
              prev.maxHp
            ),
          }
        : prev
    );
  }

  const c =
    centerInArena(dstRef);

  spawnFloater(
    `-${dmg}`,
    c.x,
    c.y - 60,
    '#ff4d4d'
  );

  statsRef.current.p1Damage += dmg;

} else {
  /*
   * OPPONENT attacked PLAYER.
   */

  if (Number.isFinite(attackerHp)) {
    setP2((prev) =>
      prev
        ? {
            ...prev,
            hp: clamp(
              attackerHp,
              0,
              prev.maxHp
            ),
          }
        : prev
    );
  }

  if (Number.isFinite(defenderHp)) {
    setP1((prev) =>
      prev
        ? {
            ...prev,
            hp: clamp(
              defenderHp,
              0,
              prev.maxHp
            ),
          }
        : prev
    );
  }

  const c =
    centerInArena(dstRef);

  spawnFloater(
    `-${dmg}`,
    c.x,
    c.y - 60,
    '#ff4d4d'
  );

  statsRef.current.p2Damage += dmg;
}

        pushLog(
          `${evt.actor === 'player' ? p1?.name || 'You' : p2?.name || 'Enemy'} hits for ${dmg}`,
        );

        setIsActing(false);

        } else if (evt.type === 'regen') {

  const heal = Number(evt.heal || 0);
  const newHp = Number(evt.hp);

  /*
   * `actor` here is the unit whose turn is starting,
   * therefore it is also the unit receiving Regen.
   */
  const regenIsPlayer =
    evt.actor === 'player';

  if (regenIsPlayer) {
    setP1((prev) => {
      if (!prev) return prev;

      const hp =
        Number.isFinite(newHp)
          ? clamp(newHp, 0, prev.maxHp)
          : clamp(
              (prev.hp ?? prev.maxHp) + heal,
              0,
              prev.maxHp
            );

      return {
        ...prev,
        hp,
      };
    });

    const ref =
      p1Ref.current;

    if (ref) {
      const c =
        centerInArena(ref);

      spawnFloater(
        `+${heal}`,
        c.x,
        c.y - 42,
        '#3cff8b'
      );
    }

    if (p1?.id) {
      flashAbility(
        p1.id,
        'regen'
      );
    }

  } else {
    setP2((prev) => {
      if (!prev) return prev;

      const hp =
        Number.isFinite(newHp)
          ? clamp(newHp, 0, prev.maxHp)
          : clamp(
              (prev.hp ?? prev.maxHp) + heal,
              0,
              prev.maxHp
            );

      return {
        ...prev,
        hp,
      };
    });

    const ref =
      p2Ref.current;

    if (ref) {
      const c =
        centerInArena(ref);

      spawnFloater(
        `+${heal}`,
        c.x,
        c.y - 42,
        '#3cff8b'
      );
    }

    if (p2?.id) {
      flashAbility(
        p2.id,
        'regen'
      );
    }
  }

  pushLog(
    `${
      regenIsPlayer
        ? p1?.name || 'You'
        : p2?.name || 'Enemy'
    } regen +${heal}`
  );

} else if (evt.type === 'dot') {

  const dotName =
    evt.dot || 'dot';

  const newHp =
    Number(evt.hp);

  const unitSide =
    getScriptUnitSide(evt);

  if (
    !unitSide ||
    !Number.isFinite(newHp)
  ) {
    console.warn(
      '[battle replay] Unable to map DOT event',
      evt
    );
  } else if (unitSide === 'player') {

    setP1((prev) => {
      if (!prev) return prev;

      const oldHp =
        Number(
          prev.hp ??
          prev.maxHp ??
          0
        );

      const finalHp =
        clamp(
          newHp,
          0,
          prev.maxHp
        );

      const dmg =
        Math.max(
          0,
          oldHp - finalHp
        );

      const ref =
        p1Ref.current;

      if (
        dmg > 0 &&
        ref
      ) {
        const c =
          centerInArena(ref);

        const icon =
          dotName === 'thorns'
            ? '🛡️'
            : dotName === 'burn'
              ? '🔥'
              : '🩸';

        spawnFloater(
          `-${dmg} ${icon}`,
          c.x,
          c.y - 42,
          '#ff6666'
        );

        pushLog(
          `${prev.name} ${dotName} -${dmg}`
        );
      }

      return {
        ...prev,
        hp: finalHp,
      };
    });

  } else {

    setP2((prev) => {
      if (!prev) return prev;

      const oldHp =
        Number(
          prev.hp ??
          prev.maxHp ??
          0
        );

      const finalHp =
        clamp(
          newHp,
          0,
          prev.maxHp
        );

      const dmg =
        Math.max(
          0,
          oldHp - finalHp
        );

      const ref =
        p2Ref.current;

      if (
        dmg > 0 &&
        ref
      ) {
        const c =
          centerInArena(ref);

        const icon =
          dotName === 'thorns'
            ? '🛡️'
            : dotName === 'burn'
              ? '🔥'
              : '🩸';

        spawnFloater(
          `-${dmg} ${icon}`,
          c.x,
          c.y - 42,
          '#ff6666'
        );

        pushLog(
          `${prev.name} ${dotName} -${dmg}`
        );
      }

      return {
        ...prev,
        hp: finalHp,
      };
    });
  }
      } else if (evt.type === 'ability') {
        const name = evt.ability || 'Ability';
        const actorIsP1 = evt.actor === 'player';
        const unitId = actorIsP1 ? p1?.id : p2?.id;
        const unit = actorIsP1 ? p1 : p2;
        const label = actorIsP1 ? p1?.name || 'You' : p2?.name || 'Enemy';

        pushLog(`${label} uses ${name}`);

        if (unitId && unit?.abilitiesSet?.has(toId(name))) {
          // ✅ only flash if this unit actually has the ability
          flashAbility(unitId, name);
          statsRef.current.abilities += 1;
        }
      } else if (evt.type === 'end') {

  const w =
    evt.winner === 'player'
      ? 1
      : 2;

  if (evt.finalHp) {

    const finalPlayerHp =
      Number(evt.finalHp.player);

    const finalOpponentHp =
      Number(evt.finalHp.opponent);

    if (Number.isFinite(finalPlayerHp)) {
      setP1((prev) =>
        prev
          ? {
              ...prev,
              hp: clamp(
                finalPlayerHp,
                0,
                prev.maxHp
              ),
            }
          : prev
      );
    }

    if (Number.isFinite(finalOpponentHp)) {
      setP2((prev) =>
        prev
          ? {
              ...prev,
              hp: clamp(
                finalOpponentHp,
                0,
                prev.maxHp
              ),
            }
          : prev
      );
    }
  }

  setWinner(w);

  pushLog(
    w === 1
      ? 'You win!'
      : 'Defeat...'
  );
}

      if (!cancelled) {
        setScriptIndex((i) => i + 1);
      }
      busyRef.current = false;
    };

    runStep();

    return () => {
      cancelled = true;
      busyRef.current = false;
    };
  }, [battleScript, scriptIndex, p1?.id, p2?.id, winner, isPaused, introActive]);

  /* ---------------- Turn driver (strict alternation) ---------------- */
  useEffect(() => {
    if (battleScript && battleScript.length > 0) return;
    if (!p1 || !p2 || winner || turn === 0) return;
    if (busyRef.current) return;
    busyRef.current = true;

    let cancelled = false;

    const runTurn = async () => {
      await waitIfPaused();

      if (impact) {
        await new Promise((r) =>
          setTimeout(r, animDur((IMPACT_LINGER_S + 0.06) * TURN_SPEED) * 1000),
        );
      }

      setIsActing(true);
      const actorIsP1 = turn === 1;
      const side = actorIsP1 ? 'left' : 'right';

      let A = actorIsP1 ? { ...p1 } : { ...p2 };
      let D = actorIsP1 ? { ...p2 } : { ...p1 };

      // start-of-turn (regen only)
      A = startOfTurn(A);

      // Purify (remove a random debuff or aegis)
      if (has(A, 'purify')) {
        const s = statusRef.current[A.id] || {};
        const keys = Object.keys(s).filter(
          (k) => s[k] && (k === 'burn' || k === 'bleed' || k === 'aegisCharges'),
        );
        if (keys.length) {
          const k = pick(keys);
          const ns = { ...s };
          delete ns[k];
          statusRef.current[A.id] = ns;
          pushLog(`${A.name} purifies ${k.replace('aegisCharges', 'aegis')}.`);
          flashAbilityWithLabel(A.id, 'purify');
          statsRef.current.abilities += 1;
          const ref = actorIsP1 ? p1Ref.current : p2Ref.current;
          const c = centerInArena(ref);
          spawnFloater('✨', c.x, c.y, '#9adcfb');
        }
      }

      await waitIfPaused(); // before windup

      await new Promise((r) => setTimeout(r, animDur(PRE_HIT_PAUSE_S * TURN_SPEED) * 1000));

      await waitIfPaused();

      let r = await attackOnce(A, D, side, 1, { canApplyStatus: true });
      A = r.A;
      D = r.D;

      // Frenzy bonus hit (reduced dmg, cannot re-apply status)
      if (has(A, 'frenzy') && Math.random() < 0.15 && D.hp > 0) {
        pushLog(`${A.name} triggers Frenzy!`);
        flashAbilityWithLabel(A.id, 'frenzy');
        statsRef.current.abilities += 1;
        await waitIfPaused();
        await new Promise((r2) => setTimeout(r2, animDur(0.14 * TURN_SPEED) * 1000));
        await waitIfPaused();
        r = await attackOnce(A, D, side, 0.6, { canApplyStatus: false });
        A = r.A;
        D = r.D;
      }

      await waitIfPaused();

      // End-of-action DoTs on defender
      {
        const sD = statusRef.current[D.id] || {};
        const toRef = actorIsP1 ? p2Ref.current : p1Ref.current;
        const ct = centerInArena(toRef);

        if (sD.burn) {
          const burn = BURN_TICK;
          if (burn > 0) {
            D.hp = clamp(D.hp - burn, 0, D.maxHp);
            spawnFloater(`-${burn} 🔥`, ct.x, ct.y - 42, '#ff6666');
            pushLog(`${D.name} burn -${burn}`);
          }
          sD.burn = Math.max(0, sD.burn - 1);
        }

        if (sD.bleed) {
          const bleed = BLEED_TICK;
          if (bleed > 0) {
            D.hp = clamp(D.hp - bleed, 0, D.maxHp);
            spawnFloater(`-${bleed} 🩸`, ct.x, ct.y - 42, '#ff3333');
            pushLog(`${D.name} bleed -${bleed}`);
          }
          sD.bleed = Math.max(0, sD.bleed - 1);
        }

        statusRef.current[D.id] = sD;
      }

      // Commit board state
      if (actorIsP1) {
        setP1(A);
        setP2(D);
      } else {
        setP2(A);
        setP1(D);
      }

      const someoneDead = A.hp <= 0 || D.hp <= 0;
      if (someoneDead) {
        const localW = D.hp <= 0 ? (actorIsP1 ? 1 : 2) : actorIsP1 ? 2 : 1;

        const forced = forcedWinnerRef.current; // 1 = player, 2 = opponent, or null
        const finalW = forced || localW; // server wins if it says so

        if (!cancelled) {
          // Force final board to match finalW
          if (finalW === 1) {
            setP1((prev) => (prev ? { ...prev, hp: Math.max(1, prev.hp || 1) } : prev));
            setP2((prev) => (prev ? { ...prev, hp: 0 } : prev));
          } else {
            setP1((prev) => (prev ? { ...prev, hp: 0 } : prev));
            setP2((prev) => (prev ? { ...prev, hp: Math.max(1, prev.hp || 1) } : prev));
          }

          setWinner(finalW);
          pushLog(finalW === 1 ? 'You win!' : 'Defeat...');
        }
        busyRef.current = false;
        return;
      }

      setIsActing(false);

      statsRef.current.turns += 1;

      if (!cancelled) setTurn((t) => (t === 1 ? 2 : 1));
      busyRef.current = false;
    };

    runTurn();
    return () => {
      cancelled = true;
      busyRef.current = false;
    };
  }, [turn, winner, p1?.id, p2?.id, isPaused, serverWinner, battleScript]); // re-run when ids or turn/winner change

  /* ---------------- Controls ---------------- */
  const rematch = async () => {
  if (isStartingRematch) return;

  if (cooldownLeft > 0) {
    const id = Math.random().toString(36).slice(2);
    enqueueNotice({
      id,
      text: `Wait ${cooldownLeft}s before the next battle`,
      color: '#ffdf5f',
      duration: 1.8,
    });
    pushLog(`Wait ${cooldownLeft}s before the next battle`);
    return;
  }

  if (limitReached) return;

  setIsStartingRematch(true);

  setAwardedRewards([]);
  resultPostedRef.current = false;
  settlementPostedRef.current = false;

  clearNotices();
  if (!p1) {
    setIsStartingRematch(false);
    return;
  }

    const localSparks = Number(user?.sparks ?? user?.credits ?? user?.balance ?? NaN);
    if (Number.isFinite(localSparks) && localSparks <= 0) {
      const id = Math.random().toString(36).slice(2);
      enqueueNotice({ id, text: 'No funds', color: '#ff6b6b', duration: 1.6 });
      pushLog('No funds');
      firstBtnRef.current?.showHint?.();
      return;
    }

    // heal player copy for visuals
    let healed = { ...p1, hp: p1.maxHp };
    delete healed._berserkedOnce;

    setIntroPlayer(healed);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/starta`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'X-TG-INITDATA': window.Telegram?.WebApp?.initData || '',
        },
        body: JSON.stringify({ cardId: healed.id, arenaId: effectiveArenaId }),
      });

      let data = await res.json().catch(() => ({}));

      if (!res.ok || !data.battleId) {
        const msg = String(data?.error || '').toLowerCase();

        const code = String(data?.error || '');

        if (code === 'HUMAN_CHECK_REQUIRED') {
          onNavigate('humancheck', {
            returnTo: 'fight',
            returnParams: {
              openedVia,
            },
          });
          return;
        }

        if (res.status === 429 && (data?.error === 'COOLDOWN' || msg.includes('cooldown'))) {
          handleCooldown(data);
          return;
        }

        if (data?.error === 'DAILY_LIMIT' || msg.includes('daily_limit')) {
          setLimitInfo({
            limit: Number(data?.limit ?? 5),
            used: Number(data?.used ?? 5),
            resetAt: data?.resetAt || null,
          });
          const id = Math.random().toString(36).slice(2);
          enqueueNotice({
            id,
            text: 'Daily limit reached for this card',
            color: '#ffb347',
            duration: 2.0,
          });
          pushLog(`Daily limit reached (${data?.used ?? '?'} / ${data?.limit ?? '?'})`);
          return;
        }

        if (
          res.status === 402 ||
          msg.includes('insufficient') ||
          msg.includes('no funds') ||
          msg.includes('spark')
        ) {
          const id = Math.random().toString(36).slice(2);
          enqueueNotice({
            id,
            text: 'No funds',
            color: '#ff6b6b',
            duration: 1.6,
          });
          pushLog('No funds');
          firstBtnRef.current?.showHint?.();
          return;
        }
        throw new Error(data?.error || 'Failed to start new battle');
      }

// Já entrou numa nova tentativa: fecha o modal e mostra Searching AGORA.
setBattleId(data.battleId);

if (data?.dailyLimit) {
  setLimitInfo({
    used: Number(data.dailyLimit.used || 0),
    limit: Number(data.dailyLimit.limit || 0),
  });
}

setPlaySig(null);
setServerWinner(null);
setBattleScript(null);
setScriptIndex(0);

setP1(healed);
setP2(null);
setWinner(null);
setLog([]);
setTurn(0);
setIntroActive(true);
setOppStatus('searching');
setArenaBg(randomBattleBg());

busyRef.current = false;

// Agora espera achar live ou cair no fallback.
data = await waitForBattleReady(data, {
  cardId: healed.id,
  arenaId: effectiveArenaId,
});

setBattleId(data.battleId);
setPlaySig(data.playSig || null);

if (data.user) {
  setUser?.((u) => ({ ...(u || {}), ...(data.user || {}) }));
}

      if (data.user) setUser?.((u) => ({ ...(u || {}), ...(data.user || {}) }));

      const sm = data?.opponent?.serverMeta;
      if (sm && (sm.winner === 'player' || sm.winner === 'opponent')) {
        setServerWinner(sm.winner === 'player' ? 1 : 2);
      } else {
        setServerWinner(null);
      }

      let foe = normalizeUnitFromApi(data?.opponent);

      setBattleScript(sm?.script || data.script || null);
      setScriptIndex(0);

      if (!foe || !foe.id) {
        pushLog('Matchmaking failed. Please try again.');
        // Do not start a local fight
        setIntroActive(false);
        setTurn(0);
        return;
      }

      const foeSource = String(foe?.serverMeta?.source || '').toLowerCase();
const shouldApplyFrontendPassiveBuffs = foeSource !== 'defense_deck';

if (shouldApplyFrontendPassiveBuffs && has(foe, 'fortify')) {
  foe.maxHp += 5;
  foe.hp += 5;
}
if (shouldApplyFrontendPassiveBuffs && has(foe, 'shield wall')) {
  foe.defense += 3;
}

      initialTurnRef.current = Math.random() < 0.5 ? 1 : 2;

      statusRef.current = {
        [healed.id]: {
          regenCharges: has(healed, 'regen') ? 1 : 0,
          aegisCharges: has(healed, 'aegis') ? 1 : 0,
        },
        [foe.id]: {
          regenCharges: has(foe, 'regen') ? 1 : 0,
          aegisCharges: has(foe, 'aegis') ? 1 : 0,
        },
      };
      appliedOnceRef.current = {
        [healed.id]: { flameburstApplied: false, bleedApplied: false },
        [foe.id]: { flameburstApplied: false, bleedApplied: false },
      };

      statsRef.current = { turns: 0, p1Damage: 0, p2Damage: 0, abilities: 0 };
      busyRef.current = false;

setP1(healed);
setP2(foe);
setWinner(null);
setTurn(0);
setIntroActive(true);
setOppStatus('found');

      return; // ← IMPORTANT: prevent fall-through to any code after try/catch
        } catch (err) {
  if (String(err?.message) === "MATCHMAKING_CANCELLED") {
    return;
  }

  console.error('[rematch] start error:', err);
  const m = String(err?.message || '').toLowerCase();

      if (m.includes('insufficient') || m.includes('no funds') || m.includes('spark')) {
        const id = Math.random().toString(36).slice(2);
        enqueueNotice({
          id,
          text: 'No funds',
          color: '#ff6b6b',
          duration: 1.6,
        });
        pushLog('No funds');
        firstBtnRef.current?.showHint?.();
        return;
      }

      pushLog('Failed to start new battle');
      return;
    } finally {
      setIsStartingRematch(false);
    }
  };

  /* ---------------- INTRO flow ---------------- */
  const SEARCH_MIN_MS = 2500;
  const introStartRef = useRef(0);

  useEffect(() => {
  if (!introActive || oppStatus !== 'searching') return;

  setSearchElapsed(0);

  const startedAt = Date.now();

  const timer = setInterval(() => {
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    setSearchElapsed(elapsed);
  }, 250);

  return () => clearInterval(timer);
}, [introActive, oppStatus, battleId]);

  useEffect(() => {
    if (introActive) introStartRef.current = Date.now();
  }, [introActive]);

  useEffect(() => {
    if (!introActive || !p1 || !p2) return;
    const elapsed = Date.now() - introStartRef.current;
    const wait = Math.max(0, SEARCH_MIN_MS - elapsed);
    const reveal = setTimeout(() => setOppStatus('found'), wait);
    return () => clearTimeout(reveal);
  }, [introActive, p1, p2]);

useEffect(() => {
  if (!introActive || oppStatus !== 'found') return;

  const t = setTimeout(() => {
    setIntroActive(false);
    setTurn(initialTurnRef.current);
  }, MATCH_FOUND_PAUSE_MS);

  return () => clearTimeout(t);
}, [introActive, oppStatus]);

  const newFightLocked =
  isSpecialBattle ||
  limitReached ||
  isStartingRematch;

  let newFightLabel = 'New Fight';

  if (limitReached) {
    newFightLabel = 'Limit Reached';
  } else if (cooldownLeft > 0) {
    newFightLabel = `Next Battle In ${cooldownLeft}s`;
  }

  const isPvpBattle =
  String(p2?.serverMeta?.source || '').toLowerCase() === 'live_pvp';

  /* ---------------- Render ---------------- */
  return (
    <div className="battle-container">
      {/* Soft background fade-in */}
      <AnimatePresence>
        {!introActive && (
          <motion.div
            key="arena-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${arenaBg})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              zIndex: 0,
            }}
          />
        )}
      </AnimatePresence>

      {/* INTRO overlay */}
      <AnimatePresence>
        {introActive ? (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="battle_splash"
          >

            {/* Player */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 240,
                damping: 22,
                delay: 0.05,
              }}
              style={{ display: 'grid', placeItems: 'center', gap: 10 }}
            >
             {(p1 || introPlayer) && (
  <BattleSplashCard
    unit={p1 || introPlayer}
    user={user}
    side="left"
    opponent={false}
    safeTop
  />
)}
            </motion.div>

            {/* VS */}
            <motion.div
              initial={{ scale: 0.2, opacity: 0 }}
              animate={{ scale: 0.8, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 260,
                damping: 18,
                delay: 0.18,
              }}
              className="battle_vs"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
            </motion.div>

            {/* Opponent */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 240,
                damping: 22,
                delay: 0.1,
              }}
              style={{ display: 'grid', placeItems: 'center', gap: 10 }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {oppStatus === 'searching' ? (
                  <motion.div
                    key="searching"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: 'grid', placeItems: 'center' }}
                  >
                    <>
  <SearchingBadge elapsed={searchElapsed} />
  {!DISABLE_MATCHMAKING_SKIP && (
  <button
    type="button"
    disabled={isSkippingMatchmaking || !battleId}
    onClick={async () => {
  try {
    setSkipButtonText('Skipping...');
    const fallbackData = await skipMatchmakingAndLoadFallback(battleId);

        if (!fallbackData?.opponent) {
          pushLog('Fallback opponent not ready yet');
          return;
        }

        setPlaySig(fallbackData.playSig || null);
        setPendingOpponent(fallbackData.opponent);

        const sm = fallbackData.opponent?.serverMeta;
        setBattleScript(sm?.script || fallbackData.script || null);
        setScriptIndex(0);

        if (sm && (sm.winner === 'player' || sm.winner === 'opponent')) {
          setServerWinner(sm.winner === 'player' ? 1 : 2);
        } else {
          setServerWinner(null);
        }

        setOppStatus('found');
      } catch (e) {
        console.warn('[skip-matchmaking] failed:', e);
        pushLog('Failed to skip matchmaking');
      }
    }}
    style={{
      marginTop: 10,
      height: 42,
      minWidth: 190,
      border: '1px solid rgba(255,255,255,0.25)',
      borderRadius: 999,
      background: 'rgba(0,0,0,0.6)',
      color: '#fff',
      fontWeight: 800,
      cursor: isSkippingMatchmaking || !battleId ? 'not-allowed' : 'pointer',
      opacity: isSkippingMatchmaking || !battleId ? 0.65 : 1,
      textShadow: '0 1px 2px #000',
    }}
  >
    {skipButtonText}
  </button>
  )}

 <div
  className="game-button red-button" style={{marginTop: 32}}
  onClick={cancelMatchmaking}
>
    CANCEL
</div>
</>
                  </motion.div>
                ) : (
                  <motion.div
                    key="found"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: -20 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: 'grid', placeItems: 'center', gap: 10 }}
                  >
                    {p2 && (
  <BattleSplashCard
    unit={p2}
    user={user}
    side="right"
    opponent
  />
)}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {!introActive && (
        <>
          {/* Arena layer */}
          <div
            ref={arenaRef}
            style={{
              position: 'relative',
              height: '100%',
              width: '100%',
              float: 'left',
              boxSizing: 'border-box',
              zIndex: 1,
            }}
          >
            {/* HUD */}
            {p1 && <HealthBar label={p1.name} hp={p1.hp} max={p1.maxHp} side="left" />}
            {p2 && <HealthBar label={p2.name} hp={p2.hp} max={p2.maxHp} side="right" />}


            {/* Turn banner */}
            {!winner && (turn === 1 || turn === 2) && (
              <motion.div
                key={turn}
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  top: 75,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 3,
                }}
              >
                <img
                  src={turn === 1 ? '/v2/turn1.png' : '/v2/turn2.png'}
                  alt={turn === 1 ? 'Your Turn' : 'Rival Turn'}
                  style={{
                    height: 42,
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />
              </motion.div>
            )}

            {/* Ability legends */}

{p1 && (
  <AbilityLegend
    unit={p1}
    side="left"
    flashes={flashMap[p1.id]}
    shieldEnabled={isArenaBattle && shieldActive}
    shieldSecondsLeft={shieldSecondsLeft}
  />
)}

{p2 && (
  <AbilityLegend
    unit={p2}
    side="right"
    flashes={flashMap[p2.id]}
    shieldEnabled={false}
  />
)}

            {/* Stage */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 90,
                bottom: 86,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                alignItems: 'center',
                justifyItems: 'center',
                padding: '0 5vw',
                boxSizing: 'border-box',
              }}
            >
              {/* Player */}
              <motion.div
                initial={false}
                animate={{
                  x: turn === 1 && !winner && isActing ? '9vw' : '0vw',
                  scale: turn === 1 && !winner && isActing ? 1.1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 240, damping: 40 }}
                style={{ justifySelf: 'start' }}
              >
                {p1 && (
                  <Avatar
                    ref={p1Ref}
                    unit={p1}
                    side="left"
                    glow={turn === 1 && !winner}
                    noBg
                    introActive={introActive}
                  />
                )}
              </motion.div>

              {/* Enemy */}
              <motion.div
                initial={false}
                animate={{
                  x: turn === 2 && !winner && isActing ? '-9vw' : '0vw',
                  scale: turn === 2 && !winner && isActing ? 1.1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 240, damping: 40 }}
                style={{ justifySelf: 'end' }}
              >
                {p2 && (
                  <Avatar
                    ref={p2Ref}
                    unit={p2}
                    side="right"
                    glow={turn === 2 && !winner}
                    noBg
                    introActive={introActive}
                  />
                )}
              </motion.div>
            </div>

            <AnimatePresence>
              {impact && (
                <ImpactBurst
                  key={impact.id}
                  x={impact.x}
                  y={impact.y}
                  type={impact.type}
                  motionOn={motionOn} // <<— add this
                />
              )}
            </AnimatePresence>

            {/* Projectile */}
            <AnimatePresence>
              {projectile && (
                <motion.div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                  }}
                  initial={false}
                >
                  {(() => {
                    const path = projectilePath || {
                      xs: [],
                      ys: [],
                      times: [],
                    };
                    const xsPts = path.xs,
                      ysPts = path.ys,
                      tms = path.times;

                    if (!xsPts.length) return null;
                    const goesLeft = projectile.from.x > projectile.to.x;

                    return (
                      <motion.div
                        initial={{
                          x: xsPts[0],
                          y: ysPts[0],
                          opacity: 0,
                          scale: 0.9,
                        }}
                        animate={{
                          x: xsPts,
                          y: ysPts,
                          opacity: [0, 1, 1, 0.95],
                          scale: [0.9, 1, 1],
                        }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: animDur(PROJ_TRAVEL_S * TURN_SPEED),
                          ease: 'linear',
                          times: tms,
                        }}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          transform: 'translate(-50%, -50%)',
                          willChange: 'transform, opacity',
                          pointerEvents: 'none',
                        }}
                      >
                        <ProjectileSprite
                          type={projectile.type}
                          size={18}
                          direction={goesLeft ? 'left' : 'right'}
                        />
                      </motion.div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>

            <FloaterLayer items={floaters} speed={TURN_SPEED} />
            <NoticeStack item={notice} speed={TURN_SPEED} />

            {/* Bottom-right floating battle controls */}
            {!winner && !isPvpBattle && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  bottom: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  zIndex: 4,
                  width: '100%',
                  pointerEvents: 'none', // panel ignores clicks…
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    pointerEvents: 'auto',
                  }}
                >
                  {/* Speed row */}
                  <div
                    style={{
                      display: 'flex',
                      gap: 6,
                      justifyContent: 'center',
                    }}
                  >
                    <button
                      onClick={() => setIsPaused((p) => !p)}
                      disabled={!!winner}
                      title={isPaused ? 'Resume' : 'Pause'}
                      aria-label={isPaused ? 'Resume' : 'Pause'}
                      style={{
                        width: 42,
                        height: 42,
                        display: 'grid',
                        placeItems: 'center',
                        border: 0,
                        background: isPaused ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                      }}
                    >
                      <img
  src={isPaused ? '/v2/pause.png' : '/v2/pause.png'}
  style={{
    width: 32,
    height: 32,
    objectFit: 'contain',
    pointerEvents: 'none'
  }}
/>
                    </button>

                    <button
                      onClick={handleSkipToEndClick}
                      disabled={!!winner}
                      title="Go to End"
                      aria-label="Go to End"
                      style={{
                        width: 42,
                        height: 42,
                        display: 'grid',
                        border: 0,
                        placeItems: 'center',
                        background: skipToEnd ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                      }}
                    >
                      <img
  src="/v2/forward.png"
  style={{
    width: 32,
    height: 32,
    objectFit: 'contain',
    pointerEvents: 'none'
  }}
/>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Victory / Defeat modal */}
            <AnimatePresence>
              {winner && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="modal_box"
                >
                <div className="modal_title">
                  {winner === 1 ? 'Victory' : 'Defeat'}
                  <div onClick={() => onNavigate(effectiveReturnTo, effectiveReturnParams)} className="modal_close"/>
                </div>
                  <div className="modal_body">
                    
                    <div className="modal_inside">
                  
                    {winner === 1 ? <TrophyIcon size={100} /> : <DefeatIcon size={100} />}
                    
                    <div
                    className="shadow2"
                      style={{
                        marginTop: 0,
                        fontWeight: 600,
                        fontSize: 21
                      }}
                    >
                      {winner === 1
                        ? `${p1?.name || 'You'} defeated ${p2?.name || 'the foe'}!`
                        : `${p2?.name || 'Enemy'} prevailed this time.`}
                    </div>

                    {isArenaBattle && shieldActive && winner !== 2 && (
                      <div
                        style={{
                          margin: '10px auto 14px',
                          padding: 12,
                          borderRadius: 12,
                          background: 'rgba(93,202,255,0.14)',
                          border: '2px solid rgba(93,202,255,0.45)',
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 900,
                            color: '#5dcaff',
                            textShadow: '0 1px 2px #000',
                            marginBottom: 6,
                            fontSize: 18,
                          }}
                        >
                          🛡️ Shield Active
                        </div>

                        <div
                          style={{
                            color: '#fff',
                            textShadow: '0 1px 2px #000',
                            fontSize: 16,
                          }}
                        >
                          Your next loss is protected.
                        </div>

                        <div
                          style={{
                            marginTop: 6,
                            color: '#ffdf5f',
                            textShadow: '0 1px 2px #000',
                            fontWeight: 800,
                            fontSize: 16,
                          }}
                        >
                          {formatCountdown(shieldSecondsLeft)} Remaining
                        </div>
                      </div>
                    )}

                    {isArenaBattle && winner === 2 && shieldLossProtected && (
                        <div
                        style={{
                          margin: '10px auto 14px',
                          padding: 12,
                          borderRadius: 12,
                          background: 'rgba(93,202,255,0.14)',
                          border: '2px solid rgba(93,202,255,0.45)',
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 900,
                            color: '#5dcaff',
                            textShadow: '0 1px 2px #000',
                            marginBottom: 6,
                            fontSize: 18,
                          }}
                        >
                          🛡️ Shield Protected You
                        </div>

                        <div
                          style={{
                            color: '#fff',
                            textShadow: '0 1px 2px #000',
                            fontSize: 16,
                          }}
                        >
                          Your Arena Trophies were protected in this loss.
                        </div>

                      </div>
                    )}

                    {winner === 1 && streakReward?.amount > 0 && (
                      <div
                        style={{
                          margin: '10px auto 14px',
                          padding: 12,
                          borderRadius: 12,
                          background: 'rgba(0,0,0,0.35)',
                          border: '1px solid rgba(255,255,255,0.18)',
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 900,
                            color: '#ffdf5f',
                            textShadow: '0 1px 2px #000',
                          }}
                        >
                          🔥 Milestone Reward!
                        </div>

                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 18,
                            color: '#9adcfb',
                            textShadow: '0 1px 2px #000',
                          }}
                        >
                          Level {streakReward.level}: +{streakReward.amount}{' '}
                          {streakReward.type === 'sparks' ? 'Sparks' : 'Runes'}
                        </div>

                        {streakReward.level === 6 && (
                          <div
                            style={{
                              marginTop: 10,
                              fontWeight: 'bold',
                              color: '#ffbb01',
                            }}
                          >
                            All 6 Levels Completed!
                          </div>
                        )}
                      </div>
                    )}

                    {winner === 1 && awardedRewards.length > 0 && (
                      <div
                        style={{
                          margin: '12px auto 14px',
                          padding: 12,
                          borderRadius: 12,
                          background: 'rgba(0,0,0,0.35)',
                          border: '1px solid rgba(255,255,255,0.18)',
                          textAlign: 'left',
                        }}
                      >
                        <div
                          style={{
                            textAlign: 'center',
                            fontWeight: 800,
                            color: '#ffdf5f',
                            marginBottom: 8,
                            textShadow: '0 1px 2px #000',
                          }}
                        >
                          {awardedRewards.some((x) => x?.isFinalFragment)
                            ? 'Item Complete!'
                            : 'New Item Added To Your Inventory'}
                        </div>

                        <div style={{ display: 'grid', gap: 8 }}>
                          {awardedRewards.map((r, idx) => (
                            <div
                              key={`${r?.itemId || 'item'}-${r?.winsRequired || 0}-${idx}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                                padding: '8px 10px',
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.12)',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  width: '100%',
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 700,
                                    textAlign: 'center',
                                  }}
                                >
                                  <span style={{ fontSize: 21, color: '#9adcfb' }}>
                                    {String(r?.itemName ?? r?.itemId ?? '')}
                                  </span>
                                </div>

                                {r?.isFinalFragment && (
                                  <div
                                    style={{
                                      marginTop: 6,
                                      fontSize: 16,
                                      fontWeight: 900,
                                      color: '#7CFFB2',
                                      textShadow: '0 1px 2px #000',
                                    }}
                                  >
                                    ✅ Completed!
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    <div
                      style={{
                        display: 'none',
                        fontSize: 13,
                        lineHeight: 1.5,
                        margin: '6px 0 14px',
                        opacity: 0.95,
                      }}
                    >
                      <div>
                        Turns: <strong>{statsRef.current.turns}</strong>
                      </div>
                      <div>
                        Your damage: <strong>{statsRef.current.p1Damage}</strong>
                      </div>
                      <div>
                        Enemy damage: <strong>{statsRef.current.p2Damage}</strong>
                      </div>
                      <div>
                        Abilities triggered: <strong>{statsRef.current.abilities}</strong>
                      </div>
                    </div>

                    {cooldownLeft > 0 && (
                      <div
                        style={{
                          margin: '15px auto 14px',
                          padding: 10,
                          borderRadius: 12,
                          background: 'rgba(0,0,0,0.35)',
                          border: '1px solid rgba(255,255,255,0.18)',
                          color: '#9adcfb',
                          fontWeight: 800,
                          textShadow: '0 1px 2px #000',
                          display: 'none',
                        }}
                      >
                        Next battle in {cooldownLeft}s
                      </div>
                    )}

                     {(
                      isChallengeBattle ||
                      showAdRewardButton ||
                      showLossRefundButton ||
                      isWatchingAd ||
                      adRewardClaimed
                    ) && (
                      <div className="info">

                    {isChallengeBattle && winner === 1 && (
  <div
    style={{
      padding: '4px 10px',
      textAlign: 'center',
    }}
  >
    <div
      className="shadow2"
      style={{
        fontWeight: 600,
        fontSize: 18,
        color: '#fff',
      }}
    >
      Challenge won!
    </div>

    <div
      style={{
        marginTop: 4,
        fontSize: 15,
        fontWeight: 600,
        color: '#ffdf5f',
        textShadow: '0 1px 2px #000',
      }}
    >
      You claimed the prize.
    </div>
  </div>
)}

                    {isChallengeBattle && winner === 2 && (
  <div
    style={{
      padding: '4px 10px',
      textAlign: 'center',
    }}
  >
    <div
      className="shadow2"
      style={{
        fontWeight: 600,
        fontSize: 18,
        color: '#fff',
      }}
    >
      They took this round.
    </div>

    <div
      style={{
        marginTop: 4,
        fontSize: 15,
        fontWeight: 600,
        color: '#ffdf5f',
        textShadow: '0 1px 2px #000',
      }}
    >
      Ready for revenge?
    </div>
  </div>
)}
                    {/* Ad Reward Section - Only show for victories */}
                   {!isSpecialBattle &&
  winner === 1 &&
  showAdRewardButton &&
  !adRewardClaimed &&
  !shieldActive &&
  !isWatchingAd && (
                      <div>
                        <div
                          style={{
                            fontWeight: 900,
                            color: '#ffdf5f',
                            textShadow: '0 1px 2px #000',
                            marginBottom: 8,
                            fontSize: 18,
                          }}
                        >
                          🎁 Double Your Rewards!
                        </div>
                        <div
                          style={{
                            color: '#9adcfb',
                            marginBottom: 10,
                            textShadow: '0 1px 2px #000',
                          }}
                        >
                          Watch a short video to get 2x rewards
                        </div>
                        <button
                          onClick={handleWatchAdClick}
                          style={{
                            background: 'linear-gradient(180deg, #ffdf5f, #ffb800)',
                            color: '#000',
                            fontWeight: 600,
                            padding: '10px 20px',
                            fontSize: 14,
                            borderRadius: 10,
                            border: '2px solid rgba(255,255,255,0.3)',
                            cursor: 'pointer',
                            textShadow: 'none',
                          }}
                        >
                          📺 Watch Video for 2x Reward
                        </button>
                      </div>
                    )}

                    {/* Loss refund ad offer - Only show for defeats */}
                   {!isSpecialBattle &&
  winner === 2 &&
  showLossRefundButton &&
  !lossRefundClaimed &&
  !isWatchingAd && (
                      <div>
                        <div
                          style={{
                            fontWeight: 900,
                            color: '#9adcfb',
                            textShadow: '0 1px 2px #000',
                            marginBottom: 8,
                            fontSize: 18,
                          }}
                        >
                          😅 Tough loss!
                        </div>
                        <div
                          style={{
                            color: '#ffdf5f',
                            marginBottom: 10,
                            textShadow: '0 1px 2px #000',
                          }}
                        >
                          Watch a short video to get {lossRefundPercent}% of your Sparks back
                        </div>
                        <button
                          onClick={() => handleWatchLossRefundAdClick(lossRefundPercent)}
                          style={{
                            background: 'linear-gradient(180deg, #9adcfb, #4fc3f7)',
                            color: '#000',
                            fontWeight: 600,
                            padding: '10px 20px',
                            fontSize: 14,
                            borderRadius: 10,
                            border: '2px solid rgba(255,255,255,0.3)',
                            cursor: 'pointer',
                            textShadow: 'none',
                          }}
                        >
                          📺 Watch Video to Refund
                        </button>
                      </div>
                    )}

                    {/* Loading indicator while ad is playing */}
                    {!isSpecialBattle && isWatchingAd && (
                      <div
                        style={{
                          margin: '15px auto',
                          padding: 14,
                          borderRadius: 12,
                          background: 'rgba(0,0,0,0.5)',
                          border: '1px solid rgba(255,255,255,0.18)',
                          textAlign: 'center',
                          color: '#9adcfb',
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <span
                            style={{
                              width: 20,
                              height: 20,
                              display: 'inline-block',
                              borderRadius: '50%',
                              border: '3px solid rgba(154,220,251,0.3)',
                              borderTopColor: '#9adcfb',
                              animation: 'spin 0.8s linear infinite',
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontWeight: 600,
                            textShadow: '0 1px 2px #000',
                          }}
                        >
                          Loading Ad...
                        </div>
                      </div>
                    )}

                                       {!isSpecialBattle && adRewardClaimed && (
                      <div
                        style={{
                          margin: '15px auto 14px',
                          padding: 14,
                          borderRadius: 12,
                          background: 'rgba(0,255,0,0.15)',
                          border: '2px solid rgba(0,255,0,0.4)',
                          textAlign: 'center',
                          color: '#7CFFB2',
                          fontWeight: 900,
                          textShadow: '0 1px 2px #000',
                        }}
                      >
                        ✅ 2x Reward Claimed!
                      </div>
                    )}

                      </div>
                    )}

                    </div>

                    {/* Actions */}
                    <div className="modal_footer">
                      
                      {!isSpecialBattle && !limitReached && (
  <SparkGuardButton
    ref={firstBtnRef}

    user={user}

    onConfirm={() => {
      if (
        newFightLocked
      ) {
        return;
      }

      rematch();
    }}

    hintText="No funds"

    disabled={
      newFightLocked
    }

    title={
      newFightLabel
    }

    className=
      "game-button green-button"

    style={{
      marginRight:
        15
    }}
  >
    {newFightLabel}
  </SparkGuardButton>
)}

                        <button
                          onClick={() => onNavigate(effectiveReturnTo, effectiveReturnParams)}
                          className="game-button red-button"
                        >
                          BACK
                        </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <ChestWonModal
            open={activeModal?.type === 'chest'}
            chest={activeModal?.type === 'chest' ? activeModal.payload : null}
            onClose={closeActiveModal}
          />

          <PrizesWonModal
            open={activeModal?.type === 'prizes'}
            prizes={activeModal?.type === 'prizes' ? activeModal.payload : []}
            onClose={closeActiveModal}
          />

          <ArenaCompletedModal
            open={activeModal?.type === 'arena'}
            info={activeModal?.type === 'arena' ? activeModal.payload : null}
            onClose={closeActiveModal}
            onGoNextArena={(nextArenaId) => onNavigate('arenas', { arenaId: nextArenaId })}
            onGoArenaHub={() =>
              onNavigate('arenas', {
                arenaIndex: effectiveReturnParams?.fromArenaIndex,
                arenaId:
                  activeModal?.payload?.arenaId ??
                  effectiveReturnParams?.arenaId ??
                  effectiveArenaId,
                openedVia: 'battle',
                viewOnly: false,
              })
            }
          />
        </>
      )}
    </div>
  );
}
